// src/pages/SuperAdminPanel.js
import React, { useEffect, useState, useMemo } from 'react';
import {
  collection, getDocs, doc, setDoc, addDoc, onSnapshot,
  serverTimestamp, Timestamp, deleteDoc, getDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { TOOLS, ROLE_LABELS, CHECKOUT_SECTIONS } from '../config/roles';
import { useAuth } from '../contexts/AuthContext';
import SystemSettingsAccordion from '../components/SystemSettingsAccordion';
import SGCPromoteDraftsPanel from '../components/admin/SGCPromoteDraftsPanel';
import CheckinPermissionsAccordion from '../components/admin/CheckinPermissionsAccordion';



// ── Invite code helpers ────────────────────────────────────────────────────────

const CODE_TTL_HOURS = 24;
const OWNER_UID = 'IOj8t19zqBRJynu45HRKqf8J0MB2';

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  page: { padding: '2rem', maxWidth: 960 },
  header: { marginBottom: '2rem' },
  title: { color: 'var(--ka-navy)', fontSize: 28, fontWeight: 700, margin: 0 },
  subtitle: { color: 'var(--ka-gray-600)', marginTop: 6, fontSize: 14 },
  sectionTitle: {
    fontSize: 16, fontWeight: 700, color: 'var(--ka-navy)',
    margin: '32px 0 12px', textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  searchWrap: { position: 'relative', marginBottom: '1.5rem' },
  searchIcon: {
    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
    fontSize: 16, pointerEvents: 'none', color: 'var(--ka-gray-400, #9ca3af)',
  },
  searchInput: {
    width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  },
  resultCount: { fontSize: 13, color: 'var(--ka-gray-600)', marginBottom: '1rem' },
  userCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    marginBottom: '1rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  userCardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', cursor: 'pointer', userSelect: 'none',
    background: '#fafafa', borderBottom: '1px solid #f3f4f6',
  },
  userInfo: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: {
    width: 38, height: 38, borderRadius: '50%', objectFit: 'cover',
    border: '2px solid var(--ka-gold)', flexShrink: 0,
  },
  avatarFallback: {
    width: 38, height: 38, borderRadius: '50%', background: 'var(--ka-navy)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--ka-gold)', fontSize: 14, fontWeight: 700, flexShrink: 0,
    border: '2px solid var(--ka-gold)',
  },
  userName: { fontWeight: 600, fontSize: 14, color: '#111827' },
  userMeta: { fontSize: 12, color: '#6b7280', marginTop: 1 },
  roleBadge: {
    fontSize: 11, padding: '2px 8px', borderRadius: 12,
    background: 'rgba(26,54,93,0.1)', color: 'var(--ka-navy)',
    fontWeight: 600, letterSpacing: '0.03em',
  },
  superAdminBadge: {
    fontSize: 11, padding: '2px 8px', borderRadius: 12,
    background: 'rgba(200,151,58,0.15)', color: 'var(--ka-gold)',
    fontWeight: 600, letterSpacing: '0.03em', border: '1px solid rgba(200,151,58,0.3)',
  },
  ownerBadge: {
    fontSize: 11, padding: '2px 8px', borderRadius: 12,
    background: 'rgba(26,54,93,0.15)', color: 'var(--ka-navy)',
    fontWeight: 600, letterSpacing: '0.03em', border: '1px solid rgba(26,54,93,0.3)',
  },
  chevron: { fontSize: 12, color: '#9ca3af', transition: 'transform 0.2s' },
  toolGrid: {
    padding: '16px 18px',
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10,
  },
  toolRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb',
  },
  toolLabel: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 500, color: '#374151' },
  toolIconImg: {
    width: 18, height: 18, objectFit: 'contain', flexShrink: 0, display: 'block',
  },
  toolSource: { fontSize: 10, color: '#9ca3af', marginTop: 1 },
  toggle: {
    position: 'relative', width: 36, height: 20, borderRadius: 10,
    border: 'none', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
  },
  toggleKnob: {
    position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%',
    background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  saveIndicator: { fontSize: 12, color: '#6b7280', padding: '8px 18px 12px', fontStyle: 'italic' },
  promoteSection: {
    padding: '14px 18px', borderTop: '1px solid #f3f4f6',
    background: '#fafafa', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
  },
  promoteInfo: { fontSize: 13, color: '#6b7280', flex: 1 },
  promoteBtn: {
    padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: 'none', transition: 'all 0.15s',
  },
  codeBox: {
    margin: '0 18px 14px',
    padding: '14px 16px',
    background: 'rgba(200,151,58,0.06)',
    border: '1px solid rgba(200,151,58,0.3)',
    borderRadius: 8,
  },
  codeLabel: { fontSize: 11, color: 'var(--ka-gold)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 },
  codeValue: { fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: 'var(--ka-navy)', letterSpacing: '0.15em' },
  codeExpiry: { fontSize: 11, color: '#9ca3af', marginTop: 4 },
  revokeBtn: {
    marginTop: 8, fontSize: 12, color: '#ef4444', background: 'none',
    border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline',
  },
  emptyState: { textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: 14 },
  loadingWrap: { textAlign: 'center', padding: '3rem', color: '#6b7280' },
  confirmBox: {
    width: '100%',
    marginTop: 10,
    padding: '12px 14px',
    background: '#fff5f5',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    fontSize: 13,
    color: '#7f1d1d',
  },
  confirmActions: { display: 'flex', gap: 8, marginTop: 10 },
};

// ─── Checkout permissions accordion styles ────────────────────────────────────

const checkoutStyles = {
  accordion: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    marginBottom: '1rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  header: (open) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', cursor: 'pointer', userSelect: 'none',
    background: open ? '#fafafa' : '#fff',
    borderBottom: open ? '1px solid #f3f4f6' : 'none',
  }),
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  icon: { fontSize: 18 },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--ka-navy)', margin: 0 },
  subtitle: { fontSize: 12, color: '#6b7280', margin: '2px 0 0' },
  body: { padding: '16px 18px' },
  intro: {
    fontSize: 13, color: '#444', lineHeight: 1.5,
    marginBottom: 16, padding: 12,
    background: 'rgba(26,54,93,0.04)',
    borderLeft: '3px solid var(--ka-navy)',
    borderRadius: 4,
  },
  sectionRow: {
    padding: '14px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  sectionRowLast: { borderBottom: 'none' },
  sectionLabelRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 8, gap: 12, flexWrap: 'wrap',
  },
  sectionLabel: {
    fontSize: 14, fontWeight: 600, color: 'var(--ka-navy)', margin: 0,
  },
  sectionDesc: {
    fontSize: 11, color: '#888', margin: '2px 0 0', fontStyle: 'italic',
  },
  countBadge: (n) => ({
    fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 10,
    background: n > 0 ? 'rgba(46,125,50,0.1)' : 'rgba(229,57,53,0.1)',
    color: n > 0 ? '#2E7D32' : '#C62828',
  }),
  pickerRow: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    minHeight: 30, padding: 6,
    background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 6,
  },
  staffPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'var(--ka-navy)', color: '#fff',
    padding: '3px 6px 3px 10px', borderRadius: 12, fontSize: 12,
  },
  removePillBtn: {
    background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
    width: 16, height: 16, borderRadius: '50%', cursor: 'pointer',
    fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, lineHeight: 1,
  },
  emptyPicker: { fontSize: 11, color: '#999', fontStyle: 'italic', padding: '4px 6px' },
  searchAddInput: {
    flex: 1, minWidth: 140,
    padding: '4px 8px', border: 'none', background: 'transparent',
    fontSize: 12, outline: 'none',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
    maxHeight: 200, overflowY: 'auto',
    zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    marginTop: 4,
  },
  dropdownItem: {
    padding: '8px 10px', fontSize: 13, cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column',
  },
  dropdownItemHover: { background: 'rgba(26,54,93,0.05)' },
  dropdownEmpty: { padding: '10px', fontSize: 12, color: '#999', fontStyle: 'italic' },
  pickerWrap: { position: 'relative' },
  finalOverrideBlock: {
    marginTop: 10, paddingLeft: 16,
    borderLeft: '3px solid var(--ka-gold)',
    background: 'rgba(200,151,58,0.04)',
    padding: '10px 14px', borderRadius: '0 4px 4px 0',
  },
  finalOverrideLabel: {
    fontSize: 12, fontWeight: 700, color: 'var(--ka-gold)',
    margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  finalOverrideHint: {
    fontSize: 11, color: '#666', margin: '0 0 8px',
    fontStyle: 'italic', lineHeight: 1.4,
  },
  saveStatus: {
    marginTop: 10, padding: 8,
    fontSize: 12, borderRadius: 4,
  },
  saveStatusSuccess: { background: '#D4EDDA', color: '#155724' },
  saveStatusError: { background: '#FFEBEE', color: '#C62828' },
  saveStatusSaving: { background: '#FFF3CD', color: '#856404' },
};

// ─── Generic collapsible section ──────────────────────────────────────────────
//
// Thin wrapper that reuses the checkout accordion chrome so every System tool
// gets a consistent, collapsible header. Contents mount only while open, so
// nothing runs on panel load until the admin expands the section. Defaults
// closed. Pass defaultOpen to override.

function CollapsibleSection({ icon, title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={checkoutStyles.accordion}>
      <div
        style={checkoutStyles.header(open)}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <div style={checkoutStyles.headerLeft}>
          {icon && <span style={checkoutStyles.icon}>{icon}</span>}
          <div>
            <h3 style={checkoutStyles.title}>{title}</h3>
            {subtitle && <p style={checkoutStyles.subtitle}>{subtitle}</p>}
          </div>
        </div>
        <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>
      {open && <div style={checkoutStyles.body}>{children}</div>}
    </div>
  );
}

// ─── Multi-select staff picker ────────────────────────────────────────────────
//
// Renders a row of selected-staff pills with an inline search-and-add input.
// Typing filters the staff list; clicking an entry adds it. Clicking the × on
// a pill removes it. Calls `onChange(uids)` whenever the selection changes.
//
// Restricting the eligible pool: pass `eligibleUids` to limit the dropdown to
// a subset (used for final-signoff override pickers, which must be a subset
// of that section's signers). Pass null/undefined to allow any staff member.

function StaffPicker({ allStaff, selectedUids, onChange, eligibleUids = null, placeholder = 'Add staff…' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);

  const eligibleSet = eligibleUids ? new Set(eligibleUids) : null;

  const selectedSet = new Set(selectedUids || []);
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allStaff
      .filter((s) => {
        if (selectedSet.has(s.uid)) return false;
        if (eligibleSet && !eligibleSet.has(s.uid)) return false;
        if (!q) return true;
        const name = (s.displayName || '').toLowerCase();
        const email = (s.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 10);
  }, [allStaff, query, selectedSet, eligibleSet]);

  const handleAdd = (uid) => {
    onChange([...(selectedUids || []), uid]);
    setQuery('');
  };
  const handleRemove = (uid) => {
    onChange((selectedUids || []).filter((u) => u !== uid));
  };

  const selectedStaff = selectedUids
    ?.map((uid) => allStaff.find((s) => s.uid === uid))
    .filter(Boolean) || [];

  return (
    <div style={checkoutStyles.pickerWrap}>
      <div style={checkoutStyles.pickerRow} onClick={() => setOpen(true)}>
        {selectedStaff.length === 0 && !query && (
          <span style={checkoutStyles.emptyPicker}>None assigned</span>
        )}
        {selectedStaff.map((staff) => (
          <span key={staff.uid} style={checkoutStyles.staffPill}>
            {staff.displayName || staff.email || staff.uid}
            <button
              type="button"
              style={checkoutStyles.removePillBtn}
              onClick={(e) => { e.stopPropagation(); handleRemove(staff.uid); }}
              aria-label="Remove"
            >×</button>
          </span>
        ))}
        <input
          type="text"
          style={checkoutStyles.searchAddInput}
          placeholder={selectedStaff.length === 0 ? placeholder : 'Add another…'}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && (query || candidates.length > 0) && (
        <div style={checkoutStyles.dropdown}>
          {candidates.length === 0 ? (
            <div style={checkoutStyles.dropdownEmpty}>
              {eligibleSet
                ? 'No eligible staff (must already be assigned to signers above).'
                : query
                ? `No staff matching "${query}".`
                : 'No staff to add.'}
            </div>
          ) : (
            candidates.map((staff) => (
              <div
                key={staff.uid}
                style={{
                  ...checkoutStyles.dropdownItem,
                  ...(hoverId === staff.uid ? checkoutStyles.dropdownItemHover : {}),
                }}
                onMouseEnter={() => setHoverId(staff.uid)}
                onMouseLeave={() => setHoverId(null)}
                onMouseDown={(e) => { e.preventDefault(); handleAdd(staff.uid); }}
              >
                <span style={{ fontWeight: 600, color: '#111' }}>
                  {staff.displayName || '(no name)'}
                </span>
                {staff.email && (
                  <span style={{ fontSize: 11, color: '#888' }}>{staff.email}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Local variable substitution — mirrors KA API's {{Variable}} rule.
// Missing variables get their placeholder stripped, per the API docs.
function renderTemplate(template, variables) {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(variables, key)
      ? String(variables[key])
      : "";
  });
}

// Preset sample data for previewing the absence notification template
const SAMPLE_ABSENCE = {
  TestPrefix:   "[TEST] ",
  StudentName:  "Sara Al-Ahmad",
  AbsenceDate:  "Wednesday, July 23",
  TeacherName:  "John Smith",
  CourseName:   "AP Biology",
  Period:       "B",
};

const DEFAULT_ABSENCE_TEMPLATE = {
  key:       "student_absence_notification",
  name:      "Unexcused Absence Notification",
  subject:   "{{TestPrefix}}Unexcused Absence — {{CourseName}} — {{AbsenceDate}}",
  htmlBody:
`<div style="font-family: Arial, sans-serif; color: #233C5F; line-height: 1.5; max-width: 600px;">
  <p style="margin: 0 0 12px 0;">Dear {{StudentName}},</p>

  <p style="margin: 0 0 12px 0;">You were marked absent on {{AbsenceDate}} by {{TeacherName}} during {{CourseName}} (Period {{Period}}).</p>

  <p style="margin: 0 0 12px 0;">If you believe this is incorrect, you have <strong>48 hours</strong> to contact {{TeacherName}} directly by replying to this email.</p>

  <p style="margin: 0;">
    Thank you,<br/>
    Office of Student Life
  </p>
</div>`,
  variables: "TestPrefix,StudentName,AbsenceDate,TeacherName,CourseName,Block",
};

// ============================================================================
// TEMPORARY: Trigger sendAdvisorDigests (advisor weekly check-in)
// Remove before rollout — scheduledAdvisorDigest replaces it.
// ============================================================================
function AdvisorDigestTrigger() {
  const [advisorEmail, setAdvisorEmail] = useState("");   // blank = ALL advisors
  const [dryRun,   setDryRun]   = useState(true);
  const [testMode, setTestMode] = useState(true);
  const [running,  setRunning]  = useState(false);
  const [result,   setResult]   = useState(null);

  const handleRun = async () => {
    if (!dryRun) {
      const scope = advisorEmail.trim() ? advisorEmail.trim() : "ALL advisors";
      const msg = `About to SEND advisor digests (${scope}). ` +
        (testMode ? "Test mode: routed to testRecipient with [TEST] prefix. " : "NOT test mode — real advisor inboxes. ") +
        "Continue?";
      if (!window.confirm(msg)) return;
    }
    setRunning(true);
    setResult(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "sendAdvisorDigests");
      const payload = { dryRun, testMode };
      if (advisorEmail.trim()) payload.advisorEmail = advisorEmail.trim();
      const res = await fn(payload);
      setResult({ ok: true, data: res?.data });
    } catch (err) {
      setResult({ ok: false, msg: err.message || "Failed" });
    } finally {
      setRunning(false);
    }
  };

  const d = result?.data;

  return (
    <>
      <p style={{ fontSize: 13, color: "#555", margin: "0 0 12px" }}>
        Assembles each advisor's advisees, summarizes the past week's attendance
        (A–H blocks) and current grades w/ 7-day delta, and sends one digest per
        advisor. Leave the email blank to run every advisor; fill it to scope to one.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 13 }}>
          <span style={{ fontWeight: 600, marginBottom: 4 }}>Advisor email (blank = all)</span>
          <input
            type="email"
            value={advisorEmail}
            placeholder="you@kingsacademy.edu.jo"
            onChange={(e) => setAdvisorEmail(e.target.value)}
            style={{ padding: 6, border: "1px solid #ccc", borderRadius: 4, minWidth: 260 }}
          />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          <span><strong>Dry run</strong> (assemble only, send nothing)</span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
          <span>Test mode (→ testRecipient, [TEST] prefix)</span>
        </label>

        <button
          onClick={handleRun}
          disabled={running}
          style={{
            padding: "8px 16px",
            background: running ? "#999" : (dryRun ? "#233C5F" : "#E83530"),
            color: "#fff", border: "none", borderRadius: 4,
            cursor: running ? "not-allowed" : "pointer", fontWeight: 600,
          }}
        >
          {running ? "Running..." : (dryRun ? "Run Dry Run" : "SEND")}
        </button>
      </div>

      {result && (
        <div style={{
          padding: 12,
          background: result.ok ? "#e8f5e9" : "#ffebee",
          border: `1px solid ${result.ok ? "#4caf50" : "#f44336"}`,
          borderRadius: 4, fontSize: 13,
        }}>
          {!result.ok ? result.msg : d && d.ok === false ? (
            <span style={{ color: "#C62828" }}>{d.error}</span>
          ) : (
            <>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {d.dryRun ? "Dry Run" : "Live Send"} — Week of {d.weekRange}
                {d.testMode && <span style={{ marginLeft: 8, color: "#A29164" }}>(test mode)</span>}
              </div>
              <div>Emails {d.dryRun ? "that would send" : "sent"}: <strong>{d.sent}</strong></div>
              <div>Advisors skipped (no advisees / no email): <strong>{d.skipped}</strong></div>
              {Array.isArray(d.advisors) && d.advisors.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>Per-advisor breakdown</summary>
                  <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
                    {d.advisors.map((a, i) => (
                      <li key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                        <strong>{a.advisor || a.advisorId}</strong> → {a.to || "—"} — {a.advisees} advisee(s), {a.flagged} flagged — {a.status}
                        {a.error && <span style={{ color: "#C62828" }}> ({a.error})</span>}
                        {a.htmlPreview && (
                          <pre style={{ fontSize: 10, background: "#fff", border: "1px solid #eee", padding: 6, marginTop: 4, maxHeight: 140, overflow: "auto", whiteSpace: "pre-wrap" }}>
                            {a.htmlPreview}
                          </pre>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

// ============================================================================
// Trigger rosterSyncNow (Veracross n8n → /students + /staff reconciliation)
// onRequest function, superadmin bearer auth. Dry run by default; a live run
// still also depends on the config dryRun flag and the deployed cert path.
// ============================================================================
function RosterSyncTrigger() {
  const [only,    setOnly]    = useState("students"); // "students" | "staff" | "both"
  const [dryRun,  setDryRun]  = useState(true);
  const [force,   setForce]   = useState(false);
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  const handleRun = async () => {
    if (!dryRun) {
      const scope = only === "both" ? "students AND staff" : only;
      const msg = `About to run a LIVE roster sync (${scope}) that WRITES to Firestore` +
        (force ? " with match/deactivate guards BYPASSED (force)" : "") +
        ". Continue?";
      if (!window.confirm(msg)) return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { getAuth } = await import("firebase/auth");
      const token = await getAuth().currentUser.getIdToken();
      const body = { dryRun, force };
      if (only !== "both") body.only = only;
      const res = await fetch(
        "https://us-central1-kings-app-74847.cloudfunctions.net/rosterSyncNow",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      setResult({ ok: res.ok, data });
    } catch (err) {
      setResult({ ok: false, data: { error: err.message || "Failed" } });
    } finally {
      setRunning(false);
    }
  };

  const per = result?.data?.results || {};

  return (
    <>
      <p style={{ fontSize: 13, color: "#555", margin: "0 0 12px" }}>
        Pulls the current roster from the Veracross n8n webhooks and reconciles{" "}
        <code>/students</code> and <code>/staff</code> (keyed by <code>sisId</code>):
        creates missing docs, reactivates returners, keeps <code>syncFields</code>{" "}
        current, and deactivates anyone absent from the pull. Ignores the nightly
        kill switch. A dry run reports what would change without writing. Note: a
        real write also requires the <code>/config/rosterSync.dryRun</code> flag to
        be false and the deployed function's cert path to reach port 5678.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 13 }}>
          <span style={{ fontWeight: 600, marginBottom: 4 }}>Population</span>
          <select
            value={only}
            onChange={(e) => setOnly(e.target.value)}
            style={{ padding: 6, border: "1px solid #ccc", borderRadius: 4 }}
          >
            <option value="students">Students only</option>
            <option value="staff">Staff only</option>
            <option value="both">Both</option>
          </select>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          <span><strong>Dry run</strong> (report only, no writes)</span>
        </label>

        {!dryRun && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            <span>Force (bypass match / deactivate guards)</span>
          </label>
        )}

        <button
          onClick={handleRun}
          disabled={running}
          style={{
            padding: "8px 16px",
            background: running ? "#999" : (dryRun ? "#233C5F" : "#E83530"),
            color: "#fff", border: "none", borderRadius: 4,
            cursor: running ? "not-allowed" : "pointer", fontWeight: 600,
          }}
        >
          {running ? "Running…" : (dryRun ? "Run Dry Run" : "RUN LIVE SYNC")}
        </button>
      </div>

      {result && (
        <div style={{
          padding: 12,
          background: result.ok ? "#e8f5e9" : "#ffebee",
          border: `1px solid ${result.ok ? "#4caf50" : "#f44336"}`,
          borderRadius: 4, fontSize: 13,
        }}>
          {result.data?.error && !result.ok ? (
            <span style={{ color: "#C62828" }}>{result.data.error}</span>
          ) : (
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {result.data.dryRun ? "Dry Run" : "Live Sync"} —{" "}
                {result.data.ok ? "OK" : "Finished with problems"}
                {result.data.force && <span style={{ marginLeft: 8, color: "#A29164" }}>(force)</span>}
              </div>

              {Object.entries(per).map(([name, r]) => (
                <div key={name} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{name}</div>
                  {r.error ? (
                    <div style={{ color: "#C62828" }}>ERROR: {r.error}</div>
                  ) : r.skipped ? (
                    <div style={{ color: "#856404" }}>skipped: {r.skipped}</div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#333", lineHeight: 1.6 }}>
                      fetched <strong>{r.fetched}</strong> · existing {r.existingDocs} ·
                      match <strong>{r.matchPct}%</strong> · create {r.created} ·
                      reactivate {r.reactivated} · update {r.updated} · unchanged {r.unchanged} ·
                      deactivate <strong>{r.deactivated}</strong> ({r.deactivatePct}%) ·
                      exempt {r.exemptSkipped}
                      {r.idFieldAutoDetected && (
                        <div style={{ color: "#856404", marginTop: 2 }}>
                          idField auto-detected ("{r.idField}") — set it in config before applying.
                        </div>
                      )}
                      {r.blocked && <div style={{ color: "#C62828", marginTop: 2 }}>BLOCKED: {r.blocked}</div>}
                      {r.deactivationsBlocked && <div style={{ color: "#856404", marginTop: 2 }}>{r.deactivationsBlocked}</div>}
                      {r.aborted && <div style={{ color: "#C62828", marginTop: 2 }}>ABORTED: {r.aborted}</div>}
                      {Array.isArray(r.warnings) && r.warnings.map((w, i) => (
                        <div key={i} style={{ color: "#856404", marginTop: 2 }}>⚠ {w}</div>
                      ))}
                      {r.applied
                        ? <div style={{ color: "#155724", marginTop: 2 }}>✔ applied {r.writes} writes</div>
                        : <div style={{ color: "#666", marginTop: 2 }}>no writes</div>}
                    </div>
                  )}
                </div>
              ))}

              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>Raw JSON</summary>
                <pre style={{ fontSize: 11, margin: "6px 0 0", maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ============================================================================
// Read-only: Roster Sync Diagnostic
// Cross-references the live webhook pull against /students and /staff to show
// exactly what a LIVE sync would create/deactivate — WITHOUT writing anything.
// The key number is "active but absent from pull" = would be deactivated.
// ============================================================================
function RosterSyncDiagnostic() {
  const [running, setRunning] = useState(false);
  const [report, setReport]   = useState(null);
  const [error, setError]     = useState(null);

  // Mirror rosterSync's status logic: a doc is inactive if status === "inactive",
  // else if legacy `active` is false / "false"; otherwise treated as active.
  const isInactive = (d) => {
    if (d.status !== undefined) return d.status === "inactive";
    if (d.active !== undefined) return d.active === false || d.active === "false";
    return false;
  };

  const analyzePopulation = async (collName, pulledIds) => {
    const snap = await getDocs(collection(db, collName));
    let active = 0, inactive = 0;
    const activeAbsent = [];   // active docs NOT in the pull -> would deactivate
    const inactiveInPull = []; // inactive docs IN the pull -> would reactivate
    snap.forEach((docSnap) => {
      const d = docSnap.data();
      const inPull = pulledIds.has(docSnap.id);
      if (isInactive(d)) {
        inactive++;
        if (inPull) inactiveInPull.push(docSnap.id);
      } else {
        active++;
        if (!inPull) {
          activeAbsent.push({
            sisId: docSnap.id,
            name: d.name || `${d.firstName || ""} ${d.lastName || ""}`.trim() || "(no name)",
            grade: d.grade || d.departments?.join(", ") || "",
          });
        }
      }
    });
    const missingDocs = [...pulledIds].filter((id) => !snap.docs.some((x) => x.id === id));
    return {
      existing: snap.size,
      active,
      inactive,
      pulled: pulledIds.size,
      wouldCreate: missingDocs.length,
      wouldDeactivate: activeAbsent.length,
      wouldReactivate: inactiveInPull.length,
      deactivatePct: active ? Math.round((activeAbsent.length / active) * 100) : 0,
      activeAbsent,
    };
  };

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setReport(null);
    try {
      // 1. Get the live pull via the working dry-run endpoint, both populations.
      const { getAuth } = await import("firebase/auth");
      const token = await getAuth().currentUser.getIdToken();
      const res = await fetch(
        "https://us-central1-kings-app-74847.cloudfunctions.net/rosterSyncNow",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ dryRun: true }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "rosterSyncNow failed");

      // The dry-run summary doesn't return the full pulled-ID set, so we fetch
      // the webhooks' fetched counts from it for a sanity cross-check, then do
      // our own membership test by re-reading via the same function is not
      // possible client-side. Instead, we reconstruct pulled IDs from a second
      // lightweight source: the function already told us counts; for exact
      // membership we call the webhooks is blocked by CORS from the browser.
      // So we approximate: use the function's per-population sample + counts.
      //
      // Because exact per-ID membership isn't exposed, we rely on the numbers
      // the dry run already computed (fetched/existing) plus a direct read to
      // classify active vs inactive. The "would deactivate" figure below is
      // computed as: active docs − matched, where matched comes from the dry run.
      const per = data.results || {};
      const out = {};
      for (const [name, r] of Object.entries(per)) {
        if (r.error || r.skipped) { out[name] = { error: r.error || r.skipped }; continue; }
        const collName = name === "students" ? "students" : "staff";
        const snap = await getDocs(collection(db, collName));
        let active = 0, inactive = 0;
        const activeList = [];
        snap.forEach((docSnap) => {
          const d = docSnap.data();
          if (isInactive(d)) inactive++;
          else { active++; activeList.push({ sisId: docSnap.id, name: d.name || "(no name)", grade: d.grade || "" }); }
        });
        // From the dry run: matched = docs in pull that already exist.
        // active − (matched that are active) ≈ active absent from pull.
        // The dry run reports `unchanged + updated + reactivated ≈ matched`.
        const matched = (r.unchanged || 0) + (r.updated || 0) + (r.reactivated || 0);
        const wouldDeactivateApprox = Math.max(active - matched, 0);
        out[name] = {
          fetched: r.fetched,
          existing: r.existingDocs,
          active,
          inactive,
          matched,
          wouldCreate: r.created,
          wouldDeactivateApprox,
          deactivatePctApprox: active ? Math.round((wouldDeactivateApprox / active) * 100) : 0,
        };
      }
      setReport(out);
    } catch (err) {
      setError(err.message || "Diagnostic failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <p style={{ fontSize: 13, color: "#555", margin: "0 0 12px" }}>
        Read-only. Runs the dry-run pull and compares it against a direct read of{" "}
        <code>/students</code> and <code>/staff</code> to estimate what a live sync
        would change — especially how many <strong>currently-active</strong> docs are
        absent from the pull (which a live run would deactivate). Writes nothing.
      </p>

      <button
        onClick={handleRun}
        disabled={running}
        style={{
          padding: "8px 16px",
          background: running ? "#999" : "#233C5F",
          color: "#fff", border: "none", borderRadius: 4,
          cursor: running ? "not-allowed" : "pointer", fontWeight: 600,
        }}
      >
        {running ? "Analyzing…" : "Run diagnostic"}
      </button>

      {error && (
        <div style={{ marginTop: 12, padding: 10, background: "#ffebee", border: "1px solid #f44336", borderRadius: 4, fontSize: 13, color: "#C62828" }}>
          {error}
        </div>
      )}

      {report && (
        <div style={{ marginTop: 12 }}>
          {Object.entries(report).map(([name, r]) => (
            <div key={name} style={{
              marginBottom: 12, padding: 12,
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13,
            }}>
              <div style={{ fontWeight: 700, color: "var(--ka-navy)", marginBottom: 6, textTransform: "capitalize" }}>
                {name}
              </div>
              {r.error ? (
                <div style={{ color: "#856404" }}>{r.error}</div>
              ) : (
                <div style={{ lineHeight: 1.7 }}>
                  <div>Webhook returned: <strong>{r.fetched}</strong></div>
                  <div>Docs in collection: <strong>{r.existing}</strong> ({r.active} active, {r.inactive} inactive)</div>
                  <div>Matched (in pull &amp; exists): <strong>{r.matched}</strong></div>
                  <div style={{ color: r.wouldCreate > 0 ? "#155724" : "#666" }}>
                    Would create: <strong>{r.wouldCreate}</strong>
                  </div>
                  <div style={{
                    marginTop: 6, padding: "6px 10px", borderRadius: 4,
                    background: r.wouldDeactivateApprox > 0 ? "#fff3cd" : "#e8f5e9",
                    color: r.wouldDeactivateApprox > 0 ? "#856404" : "#155724",
                    fontWeight: 600,
                  }}>
                    Would deactivate ≈ <strong>{r.wouldDeactivateApprox}</strong> ({r.deactivatePctApprox}% of active)
                    {r.deactivatePctApprox > 15 && " — exceeds 15% guard; a live run would BLOCK deactivations."}
                  </div>
                </div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>
            "Would deactivate" is derived from the dry-run's matched count
            (active − matched). For the exact sisId list, add PersonId membership
            to the dry-run response — but this estimate is enough to decide whether
            it's safe to write.
          </div>
        </div>
      )}
    </>
  );
}


// ============================================================================
// TEMPORARY: Trigger sendStudentAbsenceEmails
// Remove before rollout — the scheduled function will replace this.
// ============================================================================
function AbsenceEmailTrigger() {
  const [testDate, setTestDate] = useState(todayInAmman());
  const [dryRun,   setDryRun]   = useState(true);
  const [testMode, setTestMode] = useState(true);
  const [force,    setForce]    = useState(false);
  const [running,  setRunning]  = useState(false);
  const [result,   setResult]   = useState(null);

  const handleRun = async () => {
    // Extra safety confirm when going live
    if (!dryRun) {
      const msg = `About to send REAL emails for ${testDate}. ` +
        (testMode ? "[TEST] prefix will be added. " : "NO test prefix — production wording. ") +
        "Continue?";
      if (!window.confirm(msg)) return;
    }

    setRunning(true);
    setResult(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "sendStudentAbsenceEmails");
      const res = await fn({ date: testDate, dryRun, testMode, force });
      setResult({ ok: true, data: res?.data });
    } catch (err) {
      setResult({ ok: false, msg: err.message || "Failed" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <p style={{ fontSize: 13, color: "#555", margin: "0 0 12px" }}>
        Reads <code>/attendance/{testDate}/records</code>, filters unexcused absences,
        looks up templateId from <code>/config/emailTemplates.student_absence_notification</code>,
        and calls the KA API for each record.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 13 }}>
          <span style={{ fontWeight: 600, marginBottom: 4 }}>Date (Amman)</span>
          <input
            type="date"
            value={testDate}
            onChange={(e) => setTestDate(e.target.value)}
            style={{ padding: 6, border: "1px solid #ccc", borderRadius: 4 }}
          />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          <span><strong>Dry run</strong> (log only, do not send)</span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
          <span>Test mode ([TEST] prefix)</span>
        </label>

        {!dryRun && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            <span>Force (bypass "already sent")</span>
          </label>
        )}

        <button
          onClick={handleRun}
          disabled={running}
          style={{
            padding:      "8px 16px",
            background:   running ? "#999" : (dryRun ? "#233C5F" : "#E83530"),
            color:        "#fff",
            border:       "none",
            borderRadius: 4,
            cursor:       running ? "not-allowed" : "pointer",
            fontWeight:   600,
          }}
        >
          {running ? "Running..." : (dryRun ? "Run Dry Run" : "SEND FOR REAL")}
        </button>
      </div>

      {result && (
        <div style={{
          padding:      12,
          background:   result.ok ? "#e8f5e9" : "#ffebee",
          border:       `1px solid ${result.ok ? "#4caf50" : "#f44336"}`,
          borderRadius: 4,
          fontSize:     13,
        }}>
          {result.ok ? (
            <>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {result.data.dryRun ? "Dry Run" : "Live Send"} — {result.data.date}
                {result.data.testMode && <span style={{ marginLeft: 8, color: "#A29164" }}>(test mode)</span>}
              </div>
              <div>Total records for date: <strong>{result.data.totalRecords}</strong></div>
              <div>Unexcused absences: <strong>{result.data.absencesQualifying}</strong></div>
              <div>
                Emails {result.data.dryRun ? "that would send" : "sent"}:{" "}
                <strong>{result.data.dryRun ? result.data.recipientCount : result.data.emailsSent}</strong>
              </div>              {result.data.results && result.data.results.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>Per-record breakdown</summary>
                  <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
                    {result.data.results.map((r, i) => (
                      <li key={i} style={{ fontSize: 12, marginBottom: 2 }}>
                        {r.studentName} → {r.course} P{r.period} → {r.to} — {r.sent ? "sent ✓" : `not sent (${r.reason})`}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          ) : (
            result.msg
          )}
        </div>
      )}
    </>
  );
}

// ============================================================================
// TEMPORARY: Trigger pullCanvasGrades (GradeGarden → /grades ingestion)
// Remove once the scheduled version replaces it.
// ============================================================================
// ============================================================================
// TEMPORARY: Trigger pullCanvasGrades (GradeGarden → /grades ingestion)
// Paste this component into SuperAdminPanel.js (replacing the old
// GradePullTrigger), and keep the <GradePullTrigger /> render line in the
// System section. Remove once the scheduled version replaces it.
//
// Two terms, disjoint course sets. Each button pulls ONE term and writes only
// that term's slice (coursesByTerm[term]), so running both accumulates rather
// than clobbering. Dry run pulls + reports without writing.
// ============================================================================
function GradePullTrigger() {
  const TERMS = ["ALL - 2026", "Fall Term - 2026"];

  const [dryRun, setDryRun] = React.useState(true);
  const [runningTerm, setRunningTerm] = React.useState(null); // which term is in flight
  const [result, setResult] = React.useState(null);

  const handleRun = async (term) => {
    if (!dryRun && !window.confirm(`This will WRITE the "${term}" grade slice to /grades. Continue?`)) {
      return;
    }
    setRunningTerm(term);
    setResult(null);
    try {
      const { getAuth } = await import("firebase/auth");
      const token = await getAuth().currentUser.getIdToken();
      const res = await fetch(
        "https://us-central1-kings-app-74847.cloudfunctions.net/pullCanvasGrades",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ dryRun, term }),
        }
      );
      const data = await res.json();
      setResult({ ok: res.ok, term, data });
    } catch (err) {
      setResult({ ok: false, term, data: { error: err.message || "Failed" } });
    } finally {
      setRunningTerm(null);
    }
  };

  return (
    <>
      <p style={{ fontSize: 13, color: "#555", margin: "0 0 12px" }}>
        Pulls one Canvas term's course averages, inverts the feed into per-student
        records, and writes them to <code>/grades/{'{'}sisId{'}'}</code> under{" "}
        <code>coursesByTerm[term]</code>. The two terms have disjoint courses, so
        run <strong>both</strong> to get every course. Dry run inspects without writing.
      </p>

      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 12 }}>
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
        <span><strong>Dry run</strong> (pull &amp; inspect, do not write)</span>
      </label>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {TERMS.map((term) => {
          const busy = runningTerm === term;
          const anyRunning = runningTerm !== null;
          return (
            <button
              key={term}
              onClick={() => handleRun(term)}
              disabled={anyRunning}
              style={{
                padding: "8px 16px",
                background: busy ? "#999" : (dryRun ? "#233C5F" : "#E83530"),
                color: "#fff", border: "none", borderRadius: 4,
                cursor: anyRunning ? "not-allowed" : "pointer", fontWeight: 600,
              }}
            >
              {busy ? "Running…" : `${dryRun ? "Dry run" : "WRITE"}: ${term}`}
            </button>
          );
        })}
      </div>

      {result && (
        <div style={{
          marginTop: 12, padding: 12,
          background: result.ok ? "#e8f5e9" : "#ffebee",
          border: `1px solid ${result.ok ? "#4caf50" : "#f44336"}`,
          borderRadius: 4, fontSize: 13,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {result.ok
              ? (result.data.dryRun ? "Dry run" : "Write complete")
              : "Error"} — {result.term}
          </div>
          <pre style={{ fontSize: 11, margin: 0, maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap" }}>
            {JSON.stringify(result.data, null, 2)}
          </pre>
        </div>
      )}
    </>
  );
}

function EmailTemplateManager() {
  const [semanticKey, setSemanticKey] = useState(DEFAULT_ABSENCE_TEMPLATE.key);
  const [name,        setName]        = useState(DEFAULT_ABSENCE_TEMPLATE.name);
  const [subject,     setSubject]     = useState(DEFAULT_ABSENCE_TEMPLATE.subject);
  const [htmlBody,    setHtmlBody]    = useState(DEFAULT_ABSENCE_TEMPLATE.htmlBody);
  const [variables,   setVariables]   = useState(DEFAULT_ABSENCE_TEMPLATE.variables);

  const [testModePreview, setTestModePreview] = useState(true);

  // NEW: existing template state
  const [existingTemplateId, setExistingTemplateId] = useState(null);
  const [mode,               setMode]               = useState("create");  // "create" | "update"
  const [loadingExisting,    setLoadingExisting]    = useState(false);
  const [loadingRemote,      setLoadingRemote]      = useState(false);

  const [saving,   setSaving]   = useState(false);
  const [saveMsg,  setSaveMsg]  = useState(null);

  const [remoteTemplates, setRemoteTemplates] = useState(null);
  const [listLoading,     setListLoading]     = useState(false);
  const [listError,       setListError]       = useState(null);

  const sampleVars = testModePreview
    ? SAMPLE_ABSENCE
    : { ...SAMPLE_ABSENCE, TestPrefix: "" };

  const renderedSubject = renderTemplate(subject, sampleVars);
  const renderedHtml    = renderTemplate(htmlBody, sampleVars);

  // NEW: on mount / whenever semanticKey changes, check if it exists in Firestore.
  useEffect(() => {
    let alive = true;
    const check = async () => {
      setLoadingExisting(true);
      try {
        const snap = await getDoc(doc(db, "config", "emailTemplates"));
        const cfg  = snap.data()?.[semanticKey];
        if (!alive) return;
        if (cfg?.templateId) {
          setExistingTemplateId(cfg.templateId);
          setMode("update");
        } else {
          setExistingTemplateId(null);
          setMode("create");
        }
      } catch (err) {
        console.error("Failed to check existing template:", err);
      } finally {
        if (alive) setLoadingExisting(false);
      }
    };
    check();
    return () => { alive = false; };
  }, [semanticKey]);

  // NEW: fetch the actual current state of the template from KA API
  const handleLoadFromRemote = async () => {
    if (!existingTemplateId) return;
    setLoadingRemote(true);
    setSaveMsg(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "kaEmailProxy");
      const res = await fn({
        method: "GET",
        path:   `/templates/${existingTemplateId}`,
      });
      const tpl = res?.data?.data?.data || res?.data?.data || res?.data;
      if (!tpl) throw new Error("KA API returned no template body: " + JSON.stringify(res));
      setName(tpl.name || "");
      setSubject(tpl.subject || "");
      setHtmlBody(tpl.htmlBody || "");
      setVariables(tpl.variables || "");
      setSaveMsg({ ok: true, msg: `Loaded live state of "${tpl.name}" (id=${existingTemplateId})` });
    } catch (err) {
      setSaveMsg({ ok: false, msg: err.message || "Load failed" });
    } finally {
      setLoadingRemote(false);
    }
  };

  // NEW: handleSave routes to Create or Update based on mode
  const handleSave = async () => {
    if (mode === "update") return handleUpdate();
    return handleCreate();
  };

  const handleCreate = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "kaEmailProxy");

      const createRes = await fn({
        method: "POST",
        path:   "/templates",
        body:   { name, subject, htmlBody, variables },
      });

      const created = createRes?.data?.data?.data || createRes?.data?.data || createRes?.data;
      const templateId = created?.id || created?.templateId || created?._id;
      if (!templateId) {
        throw new Error("KA API response did not include a template ID: " + JSON.stringify(created));
      }

      const cfgRef = doc(db, "config", "emailTemplates");
      await setDoc(cfgRef, {
        [semanticKey]: {
          templateId,
          name,
          description:  `Template for ${semanticKey}`,
          variables:    variables.split(",").map((v) => v.trim()).filter(Boolean),
          lastSyncedAt: new Date().toISOString(),
        },
      }, { merge: true });

      setExistingTemplateId(templateId);
      setMode("update");
      setSaveMsg({
        ok:  true,
        msg: `Created. KA templateId=${templateId} synced to /config/emailTemplates.${semanticKey}`,
      });
    } catch (err) {
      setSaveMsg({ ok: false, msg: err.message || "Create failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "kaEmailProxy");

      const updateRes = await fn({
        method: "PUT",
        path:   `/templates/${existingTemplateId}`,
        body:   { name, subject, htmlBody, variables },
      });

      // Just sync the metadata refresh in Firestore — templateId doesn't change
      const cfgRef = doc(db, "config", "emailTemplates");
      await setDoc(cfgRef, {
        [semanticKey]: {
          templateId:   existingTemplateId,
          name,
          description:  `Template for ${semanticKey}`,
          variables:    variables.split(",").map((v) => v.trim()).filter(Boolean),
          lastSyncedAt: new Date().toISOString(),
        },
      }, { merge: true });

      setSaveMsg({
        ok:  true,
        msg: `Updated template "${name}" (id=${existingTemplateId}).`,
      });
    } catch (err) {
      setSaveMsg({ ok: false, msg: err.message || "Update failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleListTemplates = async () => {
    setListLoading(true);
    setListError(null);
    setRemoteTemplates(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, "us-central1"), "kaEmailProxy");
      const res = await fn({ method: "GET", path: "/templates" });
      setRemoteTemplates(
        res?.data?.data?.data?.items ||
        res?.data?.data?.data ||
        res?.data?.data ||
        res?.data,
      );
    } catch (err) {
      setListError(err.message || "Failed to list templates");
    } finally {
      setListLoading(false);
    }
  };

  return (
    <>
      {/* NEW: mode indicator */}
      <div style={{
        display:      "inline-block",
        padding:      "3px 10px",
        borderRadius: 12,
        fontSize:     12,
        fontWeight:   700,
        marginBottom: 12,
        background:   mode === "update" ? "rgba(200,151,58,0.15)" : "rgba(46,125,50,0.15)",
        color:        mode === "update" ? "#A29164" : "#2E7D32",
        border:       `1px solid ${mode === "update" ? "rgba(200,151,58,0.4)" : "rgba(46,125,50,0.4)"}`,
      }}>
        {loadingExisting
          ? "Checking..."
          : mode === "update"
            ? `Update mode — templateId=${existingTemplateId?.slice(0, 8)}…`
            : "Create mode (new template)"}
      </div>

      <p style={{ fontSize: 13, color: "#555", margin: "0 0 16px" }}>
        Compose, preview, and save email templates. Saved templates sync their
        <code style={{ padding: "0 4px" }}>templateId</code>
        to Firestore at
        <code style={{ padding: "0 4px" }}>/config/emailTemplates</code>.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Left: editor */}
        <div>
          <label style={fieldLabelStyle}>Semantic Key (Firestore key)</label>
          <input
            type="text"
            value={semanticKey}
            onChange={(e) => setSemanticKey(e.target.value)}
            style={inputStyle}
          />
          <div style={fieldHintStyle}>
            Used as the key in <code>/config/emailTemplates</code>. Change it to create a new template; keep it to update the existing one.
          </div>

          <label style={fieldLabelStyle}>Template Name (shown in KA API)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />

          <label style={fieldLabelStyle}>Subject Line</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={inputStyle}
          />

          <label style={fieldLabelStyle}>HTML Body</label>
          <textarea
            value={htmlBody}
            onChange={(e) => setHtmlBody(e.target.value)}
            style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12, minHeight: 220 }}
          />

          <label style={fieldLabelStyle}>Variables (comma-separated)</label>
          <input
            type="text"
            value={variables}
            onChange={(e) => setVariables(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Right: preview */}
        <div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
            <span style={fieldLabelStyle}>Preview:</span>
            <label style={{ fontSize: 13, cursor: "pointer" }}>
              <input
                type="radio"
                checked={testModePreview}
                onChange={() => setTestModePreview(true)}
              />{" "}
              Test mode (TestPrefix set)
            </label>
            <label style={{ fontSize: 13, cursor: "pointer" }}>
              <input
                type="radio"
                checked={!testModePreview}
                onChange={() => setTestModePreview(false)}
              />{" "}
              Production (TestPrefix omitted)
            </label>
          </div>

          <div style={{
            border:       "1px solid #d1d5db",
            borderRadius: 6,
            background:   "#f9fafb",
            padding:      12,
            marginBottom: 10,
          }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>
              Subject
            </div>
            <div style={{ fontSize: 14, color: "#233C5F", marginTop: 4, fontWeight: 600 }}>
              {renderedSubject}
            </div>
          </div>

          <div style={{
            border:       "1px solid #d1d5db",
            borderRadius: 6,
            background:   "#fff",
            padding:      12,
            minHeight:    260,
          }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>
              Body Preview
            </div>
            <div dangerouslySetInnerHTML={{ __html: renderedHtml }} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button
          onClick={handleSave}
          disabled={saving || loadingExisting}
          style={{
            padding:      "8px 16px",
            background:   saving ? "#999" : (mode === "update" ? "#A29164" : "#233C5F"),
            color:        "#fff",
            border:       "none",
            borderRadius: 4,
            cursor:       saving ? "not-allowed" : "pointer",
            fontWeight:   600,
          }}
        >
          {saving
            ? (mode === "update" ? "Updating..." : "Creating...")
            : (mode === "update" ? "Update existing template" : "Create template")}
        </button>

        {mode === "update" && (
          <button
            onClick={handleLoadFromRemote}
            disabled={loadingRemote || saving}
            style={{
              padding:      "8px 16px",
              background:   "#fff",
              color:        "#A29164",
              border:       "1px solid #A29164",
              borderRadius: 4,
              cursor:       loadingRemote ? "not-allowed" : "pointer",
              fontWeight:   600,
            }}
          >
            {loadingRemote ? "Loading..." : "↓ Load live state from KA API"}
          </button>
        )}

        <button
          onClick={handleListTemplates}
          disabled={listLoading}
          style={{
            padding:      "8px 16px",
            background:   "#fff",
            color:        "#233C5F",
            border:       "1px solid #233C5F",
            borderRadius: 4,
            cursor:       listLoading ? "not-allowed" : "pointer",
            fontWeight:   600,
          }}
        >
          {listLoading ? "Loading..." : "List existing templates"}
        </button>
      </div>

      {saveMsg && (
        <div style={{
          marginTop:    12,
          padding:      10,
          background:   saveMsg.ok ? "#e8f5e9" : "#ffebee",
          border:       `1px solid ${saveMsg.ok ? "#4caf50" : "#f44336"}`,
          borderRadius: 4,
          fontSize:     13,
        }}>
          {saveMsg.msg}
        </div>
      )}

      {listError && (
        <div style={{
          marginTop:    12,
          padding:      10,
          background:   "#ffebee",
          border:       "1px solid #f44336",
          borderRadius: 4,
          fontSize:     13,
        }}>
          {listError}
        </div>
      )}

      {remoteTemplates && (
        <div style={{
          marginTop:    12,
          padding:      10,
          background:   "#f9fafb",
          border:       "1px solid #d1d5db",
          borderRadius: 4,
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: "#233C5F", marginBottom: 6 }}>
            Templates on KA API
          </div>
          <pre style={{ fontSize: 11, margin: 0, maxHeight: 300, overflow: "auto" }}>
            {JSON.stringify(remoteTemplates, null, 2)}
          </pre>
        </div>
      )}
    </>
  );
}

// Amman is UTC+3 (no DST). Get today's date in Amman as YYYY-MM-DD.
function todayInAmman() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Amman",
    year:  "numeric",
    month: "2-digit",
    day:   "2-digit",
  }).format(new Date());
}

// Get current time in Amman as HH:MM.
function nowInAmman() {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Amman",
    hour:   "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
}

// Build an ISO timestamp treating the chosen date+time as Amman local time (UTC+3).
function ammanLocalToISO(dateStr, timeStr) {
  // dateStr = "YYYY-MM-DD", timeStr = "HH:MM"
  // Amman is UTC+3 year-round, so subtract 3 hours to get UTC.
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi]    = timeStr.split(":").map(Number);
  // Construct as if UTC, then subtract 3h to represent Amman-local as UTC.
  const utcMs = Date.UTC(y, mo - 1, d, h - 3, mi, 0);
  return new Date(utcMs).toISOString();
}


// ─── Checkout Permissions Accordion ───────────────────────────────────────────
//
// Manages /config/checkoutPointPeople. Shape:
//   {
//     sections: {
//       it: { signers: [uid, ...], rosterAdmins: [uid, ...] },
//       library: { ... },
//       ...
//     },
//     finalSignoffOverrides: {
//       osl:      [uid, uid, ...],
//       ms_final: [uid, uid, ...],
//     }
//   }
//
// Iterates CHECKOUT_SECTIONS, so new sections appear automatically.
// Sections marked `isFinalSignoff: true` (osl, ms_final, any future
// final-signoff sections) automatically render an override picker below
// their signers picker.

// Small summary block used inside the backfill results panel. Shows the
// first 25 entries inline and offers a scrollable container if there are
// more. The CSV export (handled by the parent) carries the full data, so
// truncation here is just for screen real estate.
function BackfillSummary({ label, items, color, renderItem }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const inlineLimit = 25;
  const visible = items.slice(0, inlineLimit);
  const overflow = items.length - visible.length;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontWeight: 600, color, fontSize: 12, marginBottom: 2 }}>
        {label} ({items.length})
      </div>
      <ul style={{ margin: 0, padding: '0 0 0 18px', maxHeight: 220, overflowY: 'auto' }}>
        {visible.map((it, idx) => (
          <li key={`${label}-${idx}`} style={{ fontSize: 11, lineHeight: 1.5, color: '#444' }}>
            {renderItem ? renderItem(it) : JSON.stringify(it)}
          </li>
        ))}
        {overflow > 0 && (
          <li style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
            …and {overflow} more — download the CSV for the full list
          </li>
        )}
      </ul>
    </div>
  );
}

// Convert a backfillResult object into a flat list of CSV rows.
// Each user appears in exactly one bucket. Columns:
//   bucket, email, uid, staffSisId, departments, addedSections, error
function backfillResultToCsvRows(result) {
  if (!result) return [];
  const header = [
    'bucket',
    'email',
    'uid',
    'staffSisId',
    'departments',
    'addedSections',
    'error',
  ];
  const rows = [header];
  (result.assigned || []).forEach((it) => {
    rows.push([
      'assigned',
      it.email || '',
      it.uid || '',
      it.staffSisId || '',
      (it.departments || []).join('; '),
      (it.addedSections || []).join('; '),
      '',
    ]);
  });
  (result.noStaffMatch || []).forEach((it) => {
    rows.push([
      'no_staff_match',
      it.email || '',
      it.uid || '',
      '',
      '',
      '',
      '',
    ]);
  });
  (result.noDepartmentsMapped || []).forEach((it) => {
    rows.push([
      'no_departments_mapped',
      it.email || '',
      it.uid || '',
      it.staffSisId || '',
      (it.departments || []).join('; '),
      '',
      '',
    ]);
  });
  (result.errors || []).forEach((it) => {
    rows.push([
      'error',
      it.email || '',
      it.uid || '',
      '',
      '',
      '',
      it.error || '',
    ]);
  });
  return rows;
}

// Trigger a CSV download in the browser. Self-contained helper duplicated
// from the reports page rather than imported, to keep this file standalone.
function downloadBackfillCsv(filename, rows) {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CheckoutPermissionsAccordion() {
  const [open, setOpen] = useState(false);
  const [allStaff, setAllStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | { error: '...' }

  // Backfill state — for the "Run department-based backfill" button
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillError, setBackfillError] = useState(null);
  const [backfillDryRun, setBackfillDryRun] = useState(true);

  // Load all staff (one-shot)
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'staff'));
        const list = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        // Also try /users as a fallback / supplement, since some installs
        // store staff records there
        const userSnap = await getDocs(collection(db, 'users'));
        const userList = userSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        // Merge by uid, preferring /staff data if both present
        const merged = new Map();
        userList.forEach((u) => merged.set(u.uid, u));
        list.forEach((s) => merged.set(s.uid, { ...merged.get(s.uid), ...s }));
        const final = Array.from(merged.values()).sort((a, b) =>
          (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''),
        );
        setAllStaff(final);
      } catch (err) {
        console.error('Load staff for checkout picker failed:', err);
      } finally {
        setStaffLoading(false);
      }
    };
    load();
  }, []);

  // Subscribe to config doc. Nested shape:
  //   { sections: { it: { signers, rosterAdmins }, ... },
  //     finalSignoffOverrides: { osl: [...], ms_final: [...] } }
  useEffect(() => {
    const ref = doc(db, 'config', 'checkoutPointPeople');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          // Seed empty structure (don't write yet — wait for first edit).
          // Includes an override entry for every final-signoff section.
          const empty = { sections: {}, finalSignoffOverrides: {} };
          CHECKOUT_SECTIONS.forEach((s) => {
            empty.sections[s.id] = { signers: [], rosterAdmins: [] };
            if (s.isFinalSignoff) {
              empty.finalSignoffOverrides[s.id] = [];
            }
          });
          setConfig(empty);
        } else {
          const data = snap.data() || {};
          const sections = data.sections && typeof data.sections === 'object'
            ? { ...data.sections }
            : {};
          CHECKOUT_SECTIONS.forEach((s) => {
            const cur = sections[s.id] && typeof sections[s.id] === 'object' ? sections[s.id] : {};
            sections[s.id] = {
              signers: Array.isArray(cur.signers) ? cur.signers : [],
              rosterAdmins: Array.isArray(cur.rosterAdmins) ? cur.rosterAdmins : [],
            };
          });
          const finalSignoffOverrides = data.finalSignoffOverrides &&
            typeof data.finalSignoffOverrides === 'object'
              ? { ...data.finalSignoffOverrides }
              : {};
          // Ensure every final-signoff section has an array (even if empty),
          // so the UI's mapping doesn't have to guard against undefined.
          CHECKOUT_SECTIONS.forEach((s) => {
            if (s.isFinalSignoff && !Array.isArray(finalSignoffOverrides[s.id])) {
              finalSignoffOverrides[s.id] = [];
            }
          });
          setConfig({ sections, finalSignoffOverrides });
        }
        setConfigLoading(false);
      },
      (err) => {
        console.error('checkoutPointPeople subscribe failed:', err);
        setConfigLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // Save: write the entire nested config (no merge — we're authoritative).
  const saveConfig = (next) => {
    setConfig(next); // optimistic
    setSaveStatus('saving');
    const ref = doc(db, 'config', 'checkoutPointPeople');
    setDoc(ref, next, { merge: true })
      .then(() => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus((s) => (s === 'saved' ? null : s)), 2500);
      })
      .catch((err) => {
        console.error('Save checkoutPointPeople failed:', err);
        setSaveStatus({ error: err.message || 'Save failed' });
      });
  };

  /**
   * Update one of the per-section pickers (signers OR rosterAdmins).
   *
   * @param {string} sectionId
   * @param {'signers' | 'rosterAdmins'} pickerKey
   * @param {string[]} newUids
   */
  const handleSectionPickerChange = (sectionId, pickerKey, newUids) => {
    if (!config) return;
    const nextSections = {
      ...config.sections,
      [sectionId]: {
        ...config.sections[sectionId],
        [pickerKey]: newUids,
      },
    };
    const next = { ...config, sections: nextSections };

    // If we modified a final-signoff section's signers, intersect its
    // override list with the new signer set. (You shouldn't be able to be
    // an override-user for a section you can't sign at all.) Handles any
    // final-signoff section dynamically, not just OSL.
    const sectionCfg = CHECKOUT_SECTIONS.find((s) => s.id === sectionId);
    if (sectionCfg?.isFinalSignoff && pickerKey === 'signers') {
      const signerSet = new Set(newUids);
      const existingOverrides = config.finalSignoffOverrides?.[sectionId] || [];
      next.finalSignoffOverrides = {
        ...config.finalSignoffOverrides,
        [sectionId]: existingOverrides.filter((uid) => signerSet.has(uid)),
      };
    }
    saveConfig(next);
  };

  // Run the department-based backfill. Calls backfillCheckoutAssignments
  // Cloud Function. When dryRun is true, the function returns what WOULD
  // change without writing — useful for previewing.
  const runBackfill = async (dryRun) => {
    setBackfillRunning(true);
    setBackfillError(null);
    setBackfillResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'backfillCheckoutAssignments');
      const result = await fn({ dryRun });
      setBackfillResult(result?.data || null);
    } catch (err) {
      console.error('backfillCheckoutAssignments failed:', err);
      setBackfillError(err.message || 'Backfill failed');
    } finally {
      setBackfillRunning(false);
    }
  };

  /**
   * Update the override-user list for a given final-signoff section.
   * Replaces the previous OSL-only handleOslOverrideChange — now
   * parameterized by sectionId so it works for ms_final as well.
   */
  const handleFinalOverrideChange = (sectionId, newUids) => {
    if (!config) return;
    saveConfig({
      ...config,
      finalSignoffOverrides: {
        ...config.finalSignoffOverrides,
        [sectionId]: newUids,
      },
    });
  };

  const totalAssigned = useMemo(() => {
    if (!config) return 0;
    const set = new Set();
    CHECKOUT_SECTIONS.forEach((s) => {
      const sec = config.sections?.[s.id] || {};
      (sec.signers || []).forEach((uid) => set.add(uid));
      (sec.rosterAdmins || []).forEach((uid) => set.add(uid));
    });
    return set.size;
  }, [config]);

  return (
    <div style={checkoutStyles.accordion}>
      <div
        style={checkoutStyles.header(open)}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <div style={checkoutStyles.headerLeft}>
          <span style={checkoutStyles.icon}>🎓</span>
          <div>
            <h3 style={checkoutStyles.title}>Student Checkout Permissions</h3>
            <p style={checkoutStyles.subtitle}>
              Assign staff to each signoff section. {totalAssigned > 0 && `${totalAssigned} staff assigned.`}
            </p>
          </div>
        </div>
        <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>

      {open && (
        <div style={checkoutStyles.body}>
          <div style={checkoutStyles.intro}>
            For each section below, choose which staff members are authorized
            to sign off. Anyone you add here will automatically gain access to
            both the Student Checkout and MS Student Checkout tools (since
            library/athletics/finance staff handle both upper and middle
            school). Sections marked as <strong>final clearance</strong> (OSL,
            MS Final) also have an <strong>override</strong> list — those users
            can sign that section early (before all other sections are
            complete) when the situation requires it.
          </div>

          {/* Department-based backfill */}
          <div
            style={{
              padding: 14,
              marginBottom: 14,
              background: 'rgba(200,151,58,0.06)',
              border: '1px solid rgba(200,151,58,0.3)',
              borderRadius: 6,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 4 }}>
              Department-based auto-assign
            </div>
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.5, marginBottom: 10 }}>
              Looks up each user in /staff by email and assigns them to the
              corresponding sections based on their department(s). Useful
              for first-time bulk setup. Idempotent — safe to run repeatedly.
              Run a dry-run first to preview.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => runBackfill(true)}
                disabled={backfillRunning}
                style={{
                  padding: '7px 14px',
                  border: '1px solid var(--ka-gold)',
                  background: '#fff',
                  color: 'var(--ka-navy)',
                  borderRadius: 4,
                  cursor: backfillRunning ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {backfillRunning && backfillDryRun ? 'Running…' : 'Dry run (preview)'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      'This will write department-based assignments to ' +
                        '/config/checkoutPointPeople for all users with matching ' +
                        'staff records. Existing assignments are preserved (no removals). Continue?',
                    )
                  ) {
                    setBackfillDryRun(false);
                    runBackfill(false);
                  }
                }}
                disabled={backfillRunning}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  background: backfillRunning ? '#ccc' : 'var(--ka-gold)',
                  color: 'var(--ka-navy)',
                  borderRadius: 4,
                  cursor: backfillRunning ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {backfillRunning && !backfillDryRun ? 'Running…' : 'Apply backfill'}
              </button>
            </div>

            {backfillError && (
              <div
                style={{
                  marginTop: 10,
                  padding: 8,
                  background: '#FFEBEE',
                  border: '1px solid #FFCDD2',
                  borderRadius: 4,
                  color: '#C62828',
                  fontSize: 12,
                }}
              >
                {backfillError}
              </div>
            )}

            {backfillResult && (
              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontSize: 12,
                  color: '#333',
                  maxHeight: 480,
                  overflowY: 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontWeight: 700, color: 'var(--ka-navy)' }}>
                    {backfillResult.dryRun ? 'Dry run results' : 'Backfill applied'} —{' '}
                    {backfillResult.totalUsers} user{backfillResult.totalUsers === 1 ? '' : 's'} scanned
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const rows = backfillResultToCsvRows(backfillResult);
                      const stamp = new Date().toISOString().slice(0, 10);
                      const tag = backfillResult.dryRun ? 'dryrun' : 'applied';
                      downloadBackfillCsv(
                        `checkout-backfill-${tag}-${stamp}.csv`,
                        rows,
                      );
                    }}
                    style={{
                      padding: '5px 10px',
                      background: '#fff',
                      color: 'var(--ka-navy)',
                      border: '1px solid var(--ka-navy)',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ⬇ Download full report (CSV)
                  </button>
                </div>

                <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
                  Assigned: <strong>{(backfillResult.assigned || []).length}</strong>{' · '}
                  No /staff match: <strong>{(backfillResult.noStaffMatch || []).length}</strong>{' · '}
                  No department mapping: <strong>{(backfillResult.noDepartmentsMapped || []).length}</strong>{' · '}
                  Errors: <strong>{(backfillResult.errors || []).length}</strong>
                </div>

                <BackfillSummary
                  label="Assigned"
                  items={backfillResult.assigned}
                  color="#2E7D32"
                  renderItem={(it) => (
                    <>
                      <strong>{it.email}</strong> — [{it.departments.join(', ') || 'no dept'}] →{' '}
                      [{it.addedSections.join(', ')}]
                    </>
                  )}
                />
                <BackfillSummary
                  label="No /staff record matched"
                  items={backfillResult.noStaffMatch}
                  color="#888"
                  renderItem={(it) => <strong>{it.email}</strong>}
                />
                <BackfillSummary
                  label="Staff record found, but no recognized department"
                  items={backfillResult.noDepartmentsMapped}
                  color="#856404"
                  renderItem={(it) => (
                    <>
                      <strong>{it.email}</strong> — [{it.departments.join(', ') || 'none'}]
                    </>
                  )}
                />
                <BackfillSummary
                  label="Errors"
                  items={backfillResult.errors}
                  color="#C62828"
                  renderItem={(it) => (
                    <>
                      <strong>{it.email || it.uid}</strong> — {it.error}
                    </>
                  )}
                />
              </div>
            )}
          </div>

          {(staffLoading || configLoading) && (
            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
              Loading…
            </div>
          )}

          {!staffLoading && !configLoading && config && (
            <>
              {CHECKOUT_SECTIONS.map((section, idx) => {
                const isFinalSignoff = !!section.isFinalSignoff;
                const isLast = idx === CHECKOUT_SECTIONS.length - 1;
                const sectionConfig = config.sections?.[section.id] || {};
                const signers = sectionConfig.signers || [];
                const rosterAdmins = sectionConfig.rosterAdmins || [];
                // Auto-derived sections (osl, ms_final, uco, advancement,
                // dorm) don't have manual rosters — hide the rosterAdmins
                // picker for them.
                const AUTO_SECTION_IDS = ['osl', 'ms_final', 'uco', 'advancement', 'dorm'];
                const showRosterAdmins = !AUTO_SECTION_IDS.includes(section.id);
                return (
                  <div
                    key={section.id}
                    style={{
                      ...checkoutStyles.sectionRow,
                      ...(isLast ? checkoutStyles.sectionRowLast : {}),
                    }}
                  >
                    <div style={checkoutStyles.sectionLabelRow}>
                      <div>
                        <h4 style={checkoutStyles.sectionLabel}>{section.label}</h4>
                        {section.description && (
                          <p style={checkoutStyles.sectionDesc}>
                            {section.description}
                          </p>
                        )}
                      </div>
                      <span style={checkoutStyles.countBadge(signers.length)}>
                        {signers.length} signer{signers.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    {/* Signers picker */}
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--ka-navy)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        margin: '6px 0 4px',
                      }}
                    >
                      Signers (can sign off this section)
                    </p>
                    <StaffPicker
                      allStaff={allStaff}
                      selectedUids={signers}
                      onChange={(uids) =>
                        handleSectionPickerChange(section.id, 'signers', uids)
                      }
                    />

                    {/* Roster admins picker — only for roster-driven sections */}
                    {showRosterAdmins && (
                      <>
                        <p
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'var(--ka-gold)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            margin: '12px 0 4px',
                          }}
                        >
                          Roster admins (can upload CSV / edit roster)
                        </p>
                        <StaffPicker
                          allStaff={allStaff}
                          selectedUids={rosterAdmins}
                          onChange={(uids) =>
                            handleSectionPickerChange(section.id, 'rosterAdmins', uids)
                          }
                          placeholder="Add roster admin…"
                        />
                      </>
                    )}
                    {!showRosterAdmins && (
                      <p
                        style={{
                          fontSize: 11,
                          color: '#888',
                          fontStyle: 'italic',
                          margin: '8px 0 0',
                        }}
                      >
                        Auto-derived from student attributes — no manual roster.
                      </p>
                    )}

                    {/* Final-signoff override block: shown for any section
                        marked `isFinalSignoff: true`. Replaces the old
                        hardcoded `isOsl` check. */}
                    {isFinalSignoff && (
                      <div style={checkoutStyles.finalOverrideBlock}>
                        <p style={checkoutStyles.finalOverrideLabel}>
                          {section.label} — Early-Signoff Override
                        </p>
                        <p style={checkoutStyles.finalOverrideHint}>
                          These users (must be a subset of signers above) can
                          sign off <strong>{section.label}</strong> before all
                          other sections are complete. Use sparingly — typically
                          a Dean of Students or a designated authority only.
                        </p>
                        <StaffPicker
                          allStaff={allStaff}
                          selectedUids={config.finalSignoffOverrides?.[section.id] || []}
                          onChange={(uids) => handleFinalOverrideChange(section.id, uids)}
                          eligibleUids={signers}
                          placeholder="Add override user…"
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {saveStatus === 'saving' && (
                <div style={{ ...checkoutStyles.saveStatus, ...checkoutStyles.saveStatusSaving }}>
                  Saving…
                </div>
              )}
              {saveStatus === 'saved' && (
                <div style={{ ...checkoutStyles.saveStatus, ...checkoutStyles.saveStatusSuccess }}>
                  ✓ Saved
                </div>
              )}
              {saveStatus && saveStatus.error && (
                <div style={{ ...checkoutStyles.saveStatus, ...checkoutStyles.saveStatusError }}>
                  Save failed: {saveStatus.error}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Bulk Record Creation Accordion ──────────────────────────────────────────
//
// Calls bulkCreateCheckoutRecords Cloud Function with one of three modes:
//   - 'all-grade-12'        Seniors only
//   - 'all-upper-school'    Grades 9, 10, 11, 12
//   - 'all-middle-school'   Grades 7, 8
//
// Idempotent: existing records are skipped. After each run, the response
// shows: created count, skipped (already existed), and not-found.
//
// Each button has a confirm step. Errors render inline.

const bulkStyles = {
  button: (color, busy) => ({
    padding: '9px 16px',
    border: 'none',
    background: busy ? '#ccc' : color,
    color: '#fff',
    borderRadius: 6,
    cursor: busy ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 700,
    minWidth: 200,
    textAlign: 'left',
  }),
  resultBox: {
    marginTop: 12,
    padding: 12,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    fontSize: 12,
    color: '#333',
    maxHeight: 300,
    overflowY: 'auto',
  },
  errorBox: {
    marginTop: 12,
    padding: 10,
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    borderRadius: 4,
    color: '#C62828',
    fontSize: 12,
  },
};

function BulkRecordCreationAccordion() {
  const [open, setOpen] = useState(false);
  const [runningMode, setRunningMode] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const MODES = [
    {
      key: 'all-middle-school',
      label: 'Create all Middle School (Grades 7–8)',
      color: '#00838F',
      confirmText:
        'This will create checkout records for every student in Grades 7 and 8. ' +
        'Existing records are skipped. Continue?',
    },
    {
      key: 'all-grade-12',
      label: 'Create all Grade 12 (Seniors)',
      color: '#7B1FA2',
      confirmText:
        'This will create checkout records for every Grade 12 student. ' +
        'Existing records are skipped. Continue?',
    },
    {
      key: 'all-upper-school',
      label: 'Create all Upper School (Grades 9–12)',
      color: 'var(--ka-navy)',
      confirmText:
        'This will create checkout records for every Grade 9, 10, 11, and 12 student. ' +
        'Existing records are skipped. This is the big one — continue?',
    },
  ];

  const runMode = async (mode) => {
    setRunningMode(mode.key);
    setError(null);
    setResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'bulkCreateCheckoutRecords');
      const res = await fn({ mode: mode.key });
      setResult({ mode: mode.key, label: mode.label, data: res?.data || null });
    } catch (err) {
      console.error(`bulkCreateCheckoutRecords (${mode.key}) failed:`, err);
      setError(err.message || 'Bulk create failed.');
    } finally {
      setRunningMode(null);
    }
  };

  return (
    <div style={checkoutStyles.accordion}>
      <div
        style={checkoutStyles.header(open)}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <div style={checkoutStyles.headerLeft}>
          <span style={checkoutStyles.icon}>📋</span>
          <div>
            <h3 style={checkoutStyles.title}>Bulk Record Creation</h3>
            <p style={checkoutStyles.subtitle}>
              Create end-of-year checkout records for all students in a grade band.
            </p>
          </div>
        </div>
        <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>

      {open && (
        <div style={checkoutStyles.body}>
          <div style={checkoutStyles.intro}>
            Use these buttons at the start of the end-of-year checkout window.
            Each is idempotent — students with an existing record for the
            current school year are skipped, so you can re-run safely. Upload
            your library/athletics/finance rosters first so the new records
            are populated correctly on creation.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {MODES.map((mode) => {
              const busy = !!runningMode && runningMode !== mode.key;
              const thisRunning = runningMode === mode.key;
              return (
                <button
                  key={mode.key}
                  type="button"
                  onClick={() => {
                    if (window.confirm(mode.confirmText)) runMode(mode);
                  }}
                  disabled={!!runningMode}
                  style={bulkStyles.button(mode.color, busy || thisRunning)}
                >
                  {thisRunning ? 'Running… ' : ''}{mode.label}
                </button>
              );
            })}
          </div>

          {error && <div style={bulkStyles.errorBox}>{error}</div>}

          {result && result.data && (
            <div style={bulkStyles.resultBox}>
              <div style={{ fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 6 }}>
                {result.label} — done
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Created:</strong> {result.data.createdCount} new record
                {result.data.createdCount === 1 ? '' : 's'}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Skipped (already existed):</strong> {result.data.skippedCount}
              </div>
              {result.data.notFoundCount > 0 && (
                <div style={{ marginBottom: 4, color: '#C62828' }}>
                  <strong>Not found in /students:</strong>{' '}
                  {result.data.notFoundCount}{' '}
                  ({(result.data.notFound || []).slice(0, 10).join(', ')}
                  {result.data.notFoundCount > 10 ? ', …' : ''})
                </div>
              )}
              <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginTop: 6 }}>
                School year: {result.data.schoolYear}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tool icon helper (emoji or image) ──────────────────────────────────────────

function ToolIcon({ tool }) {
  if (tool.iconType === 'image') {
    return <img src={tool.icon} alt="" style={styles.toolIconImg} />;
  }
  // Auto-detect image paths starting with '/'
  if (typeof tool.icon === 'string' && tool.icon.startsWith('/')) {
    return <img src={tool.icon} alt="" style={styles.toolIconImg} />;
  }
  return <span>{tool.icon}</span>;
}

// ── Toggle switch ──────────────────────────────────────────────────────────────

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      style={{ ...styles.toggle, background: on ? 'var(--ka-navy)' : '#d1d5db', opacity: disabled ? 0.5 : 1 }}
      title={on ? 'Click to disable' : 'Click to enable'}
    >
      <div style={{ ...styles.toggleKnob, left: on ? 18 : 2 }} />
    </button>
  );
}

// ─── Role & Departments editor (per-user) ──────────────────────────────────
//
// Renders a small block on the user card with:
//   - A role selector: Faculty / Student / Dean (each with a confirm step)
//   - A pill-list of the user's current departments from /staff
//   - A dropdown to add departments (recognized list + Custom option)
//
// The role change calls `setUserRole` (which now accepts 'faculty',
// 'student', or 'dean'). Department edits call `updateStaffDepartments` with
// dryRun=true first to surface a section add/remove preview, then the admin
// confirms to apply.
//
// Departments live on /staff/{sisId}.departments, which is keyed by the
// user's staffSisId. If the user has no staff record, the department UI
// shows a notice and disables editing.

function RoleAndDepartmentsBlock({
  userData,
  recognizedDepartments,   // [{key, sections}, ...] from getRecognizedDepartments
  recognizedDepartmentsLoading,
  onRoleChange,            // (newRole) => updates parent
}) {
  const { user: currentUser } = useAuth();
  const isOwner = userData.uid === OWNER_UID;
  const isCurrentUser = currentUser?.uid === userData.uid;
  const isSuperAdmin = userData.role === 'superadmin';
  const protectedAccount = isOwner || isCurrentUser || isSuperAdmin;

  // Role change state. roleConfirmTarget is the role the admin is about to
  // switch this user TO (one of 'faculty' | 'student' | 'dean'), or null.
  const [roleFlipping, setRoleFlipping] = useState(false);
  const [roleConfirmTarget, setRoleConfirmTarget] = useState(null);
  const [roleError, setRoleError] = useState(null);

  // The selectable target roles and their human labels. Dean is faculty-plus:
  // it keeps tool grants and adds cross-advisor visibility in the advisory
  // tool (enforced server-side).
  const ROLE_TARGETS = [
    { key: 'faculty', label: 'Faculty' },
    { key: 'student', label: 'Student' },
    { key: 'dean', label: 'Dean' },
  ];

  // Per-target confirm copy — what changes when converting to this role.
  const roleConfirmCopy = (target) => {
    if (target === 'student') {
      return 'They will lose all checkout signoff permissions and granted tools. This is reversible.';
    }
    if (target === 'dean') {
      return 'Deans keep faculty tool access AND can view every advisor\u2019s advisees and all-student grade/attendance data in the Advisory tool. Grant only to actual deans.';
    }
    // faculty
    return 'They\u2019ll appear in faculty lists with standard tool access. Run the department backfill afterward if they need checkout sections.';
  };

  // Department state
  const [staffDoc, setStaffDoc] = useState(null);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffMissing, setStaffMissing] = useState(false);
  const [departments, setDepartments] = useState([]);

  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');

  // Preview/confirm state for dept changes
  const [pendingPreview, setPendingPreview] = useState(null);
  const [pendingNewDepartments, setPendingNewDepartments] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deptError, setDeptError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Load the staff doc on mount. Prefer staffSisId; fall back to email lookup.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      setStaffLoading(true);
      setStaffMissing(false);
      try {
        let sis = userData.staffSisId ? String(userData.staffSisId) : null;
        let snap = null;
        if (sis) {
          snap = await getDocs(collection(db, 'staff'));
          const found = snap.docs.find((d) => d.id === sis);
          if (found) snap = found;
          else snap = null;
        }
        // Fallback: email search if no sis
        if (!snap && userData.email) {
          const all = await getDocs(collection(db, 'staff'));
          const norm = userData.email.trim().toLowerCase();
          const found = all.docs.find(
            (d) => (d.data().email || '').trim().toLowerCase() === norm,
          );
          if (found) {
            sis = found.id;
            snap = found;
          }
        }
        if (!alive) return;
        if (!snap) {
          setStaffMissing(true);
          setStaffDoc(null);
          setDepartments([]);
        } else {
          const data = snap.data();
          setStaffDoc({ sisId: sis, ...data });
          setDepartments(Array.isArray(data.departments) ? [...data.departments] : []);
        }
      } catch (err) {
        console.error('Load staff doc failed:', err);
        if (alive) {
          setStaffMissing(true);
          setStaffDoc(null);
        }
      } finally {
        if (alive) setStaffLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [userData.uid, userData.email, userData.staffSisId]);

  const flipRole = async (newRole) => {
    setRoleFlipping(true);
    setRoleError(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'setUserRole');
      const res = await fn({ userUid: userData.uid, newRole });
      if (res?.data?.ok) {
        onRoleChange(newRole);
        setRoleConfirmTarget(null);
      } else {
        setRoleError('Unexpected response from server.');
      }
    } catch (err) {
      console.error('setUserRole failed:', err);
      setRoleError(err.message || 'Role change failed.');
    } finally {
      setRoleFlipping(false);
    }
  };

  // Compute the proposed new department list and call updateStaffDepartments
  // in dryRun mode to get a preview of section adds/removes.
  const requestPreview = async (proposedDepartments) => {
    setPreviewing(true);
    setDeptError(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'updateStaffDepartments');
      const res = await fn({
        userUid: userData.uid,
        newDepartments: proposedDepartments,
        dryRun: true,
      });
      if (res?.data?.ok && res.data.preview) {
        setPendingPreview(res.data.preview);
        setPendingNewDepartments(proposedDepartments);
      } else {
        setDeptError('Unexpected preview response.');
      }
    } catch (err) {
      console.error('updateStaffDepartments dryRun failed:', err);
      setDeptError(err.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const applyPending = async () => {
    if (!pendingNewDepartments) return;
    setApplying(true);
    setDeptError(null);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'updateStaffDepartments');
      const res = await fn({
        userUid: userData.uid,
        newDepartments: pendingNewDepartments,
        dryRun: false,
      });
      if (res?.data?.ok) {
        // Commit local state
        setDepartments(pendingNewDepartments);
        setPendingPreview(null);
        setPendingNewDepartments(null);
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2400);
      } else {
        setDeptError('Unexpected response on apply.');
      }
    } catch (err) {
      console.error('updateStaffDepartments apply failed:', err);
      setDeptError(err.message || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const cancelPending = () => {
    setPendingPreview(null);
    setPendingNewDepartments(null);
    setDeptError(null);
  };

  const handleAddDepartment = (deptKey) => {
    if (!deptKey) return;
    setAddPickerOpen(false);
    setCustomMode(false);
    setCustomValue('');
    // Dedup case-insensitively against current list
    const lower = deptKey.toLowerCase();
    if (departments.some((d) => d.toLowerCase() === lower)) return;
    const proposed = [...departments, deptKey];
    requestPreview(proposed);
  };

  const handleRemoveDepartment = (deptKey) => {
    const proposed = departments.filter((d) => d !== deptKey);
    requestPreview(proposed);
  };

  const submitCustom = () => {
    const v = customValue.trim();
    if (!v) return;
    handleAddDepartment(v);
  };

  // Recognized but not currently selected
  const availableForDropdown = (recognizedDepartments || []).filter((rd) => {
    const lower = rd.key.toLowerCase();
    return !departments.some((d) => d.toLowerCase() === lower);
  });

  return (
    <div style={roleDeptStyles.wrap}>
      {/* ── Role selector ─────────────────────────────────────────────── */}
      <div style={roleDeptStyles.section}>
        <div style={roleDeptStyles.sectionLabel}>Role</div>
        <div style={roleDeptStyles.roleRow}>
          <span style={roleDeptStyles.currentRole}>
            {ROLE_LABELS[userData.role] || userData.role || 'No role'}
          </span>

          {!protectedAccount && roleConfirmTarget === null && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ROLE_TARGETS.filter((t) => t.key !== userData.role).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setRoleConfirmTarget(t.key)}
                  style={roleDeptStyles.flipBtn}
                >
                  Make {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {!protectedAccount && roleConfirmTarget !== null && (
          <div style={roleDeptStyles.confirmBox}>
            <strong>
              Convert to {ROLE_LABELS[roleConfirmTarget] || roleConfirmTarget}?
            </strong>{' '}
            {roleConfirmCopy(roleConfirmTarget)}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button
                type="button"
                onClick={() => flipRole(roleConfirmTarget)}
                disabled={roleFlipping}
                style={{ ...roleDeptStyles.flipBtn, background: '#ef4444', color: '#fff' }}
              >
                {roleFlipping
                  ? 'Working\u2026'
                  : `Yes, make ${ROLE_LABELS[roleConfirmTarget] || roleConfirmTarget}`}
              </button>
              <button
                type="button"
                onClick={() => { setRoleConfirmTarget(null); setRoleError(null); }}
                style={{ ...roleDeptStyles.flipBtn, background: '#e5e7eb', color: '#374151' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {roleError && <div style={roleDeptStyles.error}>{roleError}</div>}
        {protectedAccount && (
          <div style={roleDeptStyles.hint}>
            {isOwner
              ? 'Owner account \u2014 role is fixed.'
              : isCurrentUser
              ? 'Your own account \u2014 you can\u2019t change your own role.'
              : 'Super Admin \u2014 use Revoke Super Admin first to change role.'}
          </div>
        )}
      </div>

      {/* ── Departments ───────────────────────────────────────────────── */}
      <div style={roleDeptStyles.section}>
        <div style={roleDeptStyles.sectionLabel}>Departments</div>

        {staffLoading && (
          <div style={roleDeptStyles.hint}>Loading staff record…</div>
        )}

        {!staffLoading && staffMissing && (
          <div style={roleDeptStyles.hint}>
            No <code>/staff</code> record found for this user. Departments can
            only be edited for users with a matching staff record. Add them to{' '}
            <code>/staff</code> first (manually in Firebase console), then
            refresh this panel.
          </div>
        )}

        {!staffLoading && !staffMissing && (
          <>
            <div style={roleDeptStyles.pillRow}>
              {departments.length === 0 && (
                <span style={roleDeptStyles.emptyPill}>No departments assigned</span>
              )}
              {departments.map((d) => (
                <span key={d} style={roleDeptStyles.deptPill}>
                  {d}
                  <button
                    type="button"
                    onClick={() => handleRemoveDepartment(d)}
                    style={roleDeptStyles.pillRemove}
                    title="Remove department"
                    disabled={previewing || applying}
                  >
                    ×
                  </button>
                </span>
              ))}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => { setAddPickerOpen((v) => !v); setCustomMode(false); }}
                  style={roleDeptStyles.addBtn}
                  disabled={previewing || applying || recognizedDepartmentsLoading}
                >
                  + Add department
                </button>
                {addPickerOpen && (
                  <div style={roleDeptStyles.dropdown}>
                    {customMode ? (
                      <div style={{ padding: 8 }}>
                        <input
                          type="text"
                          autoFocus
                          value={customValue}
                          onChange={(e) => setCustomValue(e.target.value)}
                          placeholder="Custom department name"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') submitCustom();
                            if (e.key === 'Escape') { setCustomMode(false); setCustomValue(''); }
                          }}
                          style={roleDeptStyles.customInput}
                        />
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button
                            type="button"
                            onClick={submitCustom}
                            style={roleDeptStyles.smallBtn}
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={() => { setCustomMode(false); setCustomValue(''); }}
                            style={{ ...roleDeptStyles.smallBtn, background: '#e5e7eb', color: '#374151' }}
                          >
                            Cancel
                          </button>
                        </div>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
                          Custom departments are saved but won't auto-map to
                          any checkout section. If you need a mapping, ask
                          to extend the department list.
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={roleDeptStyles.dropdownHeader}>Recognized departments</div>
                        {availableForDropdown.length === 0 ? (
                          <div style={roleDeptStyles.dropdownEmpty}>
                            All recognized departments already assigned.
                          </div>
                        ) : (
                          availableForDropdown.map((rd) => (
                            <div
                              key={rd.key}
                              style={roleDeptStyles.dropdownItem}
                              onClick={() => handleAddDepartment(rd.key)}
                            >
                              <div style={{ fontWeight: 600, fontSize: 12 }}>{rd.key}</div>
                              <div style={{ fontSize: 10, color: '#888' }}>
                                → {rd.sections.join(', ')}
                              </div>
                            </div>
                          ))
                        )}
                        <div
                          style={{ ...roleDeptStyles.dropdownItem, borderTop: '1px solid #e5e7eb' }}
                          onClick={() => setCustomMode(true)}
                        >
                          <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--ka-gold)' }}>
                            + Custom department…
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {previewing && (
              <div style={roleDeptStyles.hint}>Previewing change…</div>
            )}
            {deptError && (
              <div style={roleDeptStyles.error}>{deptError}</div>
            )}
            {savedFlash && (
              <div style={roleDeptStyles.success}>✓ Saved</div>
            )}

            {/* Confirm dialog with preview */}
            {pendingPreview && (
              <div style={roleDeptStyles.previewBox}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ka-navy)', marginBottom: 6 }}>
                  Preview changes
                </div>
                <div style={{ fontSize: 12, color: '#444', marginBottom: 6 }}>
                  Departments:{' '}
                  <span style={{ color: '#888' }}>
                    [{pendingPreview.oldDepartments.join(', ') || 'none'}]
                  </span>
                  {' → '}
                  <strong>[{pendingPreview.newDepartments.join(', ') || 'none'}]</strong>
                </div>
                {pendingPreview.sectionsToAdd.length > 0 && (
                  <div style={{ fontSize: 12, color: '#1e7a3a', marginBottom: 2 }}>
                    <strong>+ Sections gained:</strong> {pendingPreview.sectionsToAdd.join(', ')}
                  </div>
                )}
                {pendingPreview.sectionsToRemove.length > 0 && (
                  <div style={{ fontSize: 12, color: '#b21515', marginBottom: 2 }}>
                    <strong>− Sections lost:</strong> {pendingPreview.sectionsToRemove.join(', ')}
                  </div>
                )}
                {pendingPreview.sectionsToAdd.length === 0 &&
                  pendingPreview.sectionsToRemove.length === 0 && (
                    <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                      No section assignment changes.
                    </div>
                  )}
                {pendingPreview.unrecognizedDepartments.length > 0 && (
                  <div style={{ fontSize: 11, color: '#856404', marginTop: 4, fontStyle: 'italic' }}>
                    Note: [{pendingPreview.unrecognizedDepartments.join(', ')}] don't map to any
                    section. They'll be saved but won't affect checkout permissions.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={applyPending}
                    disabled={applying}
                    style={{ ...roleDeptStyles.flipBtn, background: 'var(--ka-navy)', color: '#fff' }}
                  >
                    {applying ? 'Applying…' : 'Apply'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelPending}
                    style={{ ...roleDeptStyles.flipBtn, background: '#e5e7eb', color: '#374151' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const roleDeptStyles = {
  wrap: {
    padding: '14px 18px',
    borderTop: '1px solid #f3f4f6',
    background: '#fcfcfd',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  section: {},
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--ka-navy)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  roleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  currentRole: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
    padding: '4px 10px',
    background: '#f3f4f6',
    borderRadius: 12,
  },
  flipBtn: {
    padding: '7px 14px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid var(--ka-navy)',
    background: '#fff',
    color: 'var(--ka-navy)',
  },
  confirmBox: {
    width: '100%',
    marginTop: 6,
    padding: 10,
    background: '#fff5f5',
    border: '1px solid #fca5a5',
    borderRadius: 6,
    fontSize: 12,
    color: '#7f1d1d',
  },
  hint: {
    fontSize: 11,
    color: '#888',
    fontStyle: 'italic',
    marginTop: 4,
  },
  error: {
    marginTop: 6,
    padding: 8,
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    color: '#C62828',
    fontSize: 12,
    borderRadius: 4,
  },
  success: {
    marginTop: 6,
    padding: 8,
    background: '#D4EDDA',
    color: '#155724',
    fontSize: 12,
    borderRadius: 4,
  },
  pillRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    minHeight: 30,
  },
  emptyPill: {
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
    padding: '2px 0',
  },
  deptPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: 'var(--ka-navy)',
    color: '#fff',
    padding: '3px 6px 3px 10px',
    borderRadius: 12,
    fontSize: 12,
  },
  pillRemove: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#fff',
    width: 16,
    height: 16,
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    lineHeight: 1,
  },
  addBtn: {
    padding: '4px 10px',
    border: '1px dashed var(--ka-navy)',
    background: '#fff',
    color: 'var(--ka-navy)',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  dropdown: {
    position: 'absolute',
    top: '110%',
    left: 0,
    width: 280,
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    maxHeight: 280,
    overflowY: 'auto',
    zIndex: 20,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  dropdownHeader: {
    padding: '6px 10px',
    fontSize: 10,
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    background: '#fafafa',
    borderBottom: '1px solid #f3f4f6',
  },
  dropdownItem: {
    padding: '8px 10px',
    cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6',
  },
  dropdownEmpty: {
    padding: '10px',
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
  customInput: {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  },
  smallBtn: {
    padding: '5px 10px',
    border: 'none',
    background: 'var(--ka-navy)',
    color: '#fff',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  previewBox: {
    marginTop: 10,
    padding: 12,
    background: '#fff8e1',
    border: '1px solid #ffd54f',
    borderRadius: 6,
  },
};

// ── User card ──────────────────────────────────────────────────────────────────

function UserCard({ userData, recognizedDepartments, recognizedDepartmentsLoading }) {
  const { user: currentUser } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [allowedTools, setAllowedTools] = useState(userData.allowedTools || []);
  const [saving, setSaving] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);

  // Nomination + invite code state
  const [nominated, setNominated] = useState(userData.superAdminNominated || false);
  const [inviteCode, setInviteCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Revoke superadmin confirmation state
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revokingSuperAdmin, setRevokingSuperAdmin] = useState(false);
  const [currentRole, setCurrentRole] = useState(userData.role);

  const isOwner = userData.uid === OWNER_UID;
  const isCurrentUser = currentUser?.uid === userData.uid;
  const isAlreadySuperAdmin = currentRole === 'superadmin';

  // A user is protected from revocation if they are the owner or the current user
  const isRevocationProtected = isOwner || isCurrentUser;

  const initials = userData.displayName
    ? userData.displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : userData.email?.[0]?.toUpperCase() || '?';

 const isToolOn = (tool) => {
  if (userData.role === 'student') return false;
  return tool.default || allowedTools.includes(tool.id);
};

  const handleToolToggle = async (tool) => {
    if (tool.default) return;
    const newAllowed = allowedTools.includes(tool.id)
      ? allowedTools.filter(id => id !== tool.id)
      : [...allowedTools, tool.id];
    setAllowedTools(newAllowed);
    setSaving(true);
    await setDoc(doc(db, 'users', userData.uid), { allowedTools: newAllowed }, { merge: true });
    setSaving(false);
    setSavedRecently(true);
    setTimeout(() => setSavedRecently(false), 2000);
  };

  const handleNominate = async () => {
    await setDoc(doc(db, 'users', userData.uid), { superAdminNominated: true }, { merge: true });
    setNominated(true);
  };

  const handleRevokeNomination = async () => {
    setRevoking(true);
    if (inviteCode) {
      await deleteDoc(doc(db, 'superAdminInvites', inviteCode.code));
      setInviteCode(null);
    }
    await setDoc(doc(db, 'users', userData.uid), { superAdminNominated: false }, { merge: true });
    setNominated(false);
    setRevoking(false);
  };

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    if (inviteCode) {
      await deleteDoc(doc(db, 'superAdminInvites', inviteCode.code));
    }
    const code = generateCode();
    const expiresAt = Timestamp.fromDate(
      new Date(Date.now() + CODE_TTL_HOURS * 60 * 60 * 1000)
    );
    await setDoc(doc(db, 'superAdminInvites', code), {
      code,
      targetUid: userData.uid,
      targetEmail: userData.email,
      createdAt: serverTimestamp(),
      expiresAt,
    });
    setInviteCode({ code, expiresAt: expiresAt.toDate() });
    setGeneratingCode(false);
  };

  // Revoke an existing superadmin back to faculty
  const handleRevokeSuperAdmin = async () => {
    setRevokingSuperAdmin(true);
    await setDoc(
      doc(db, 'users', userData.uid),
      { role: 'faculty', superAdminNominated: false },
      { merge: true }
    );
    setCurrentRole('faculty');
    setConfirmRevoke(false);
    setRevokingSuperAdmin(false);
  };

  const hasGrants = allowedTools.length > 0;

  return (
    <div style={styles.userCard}>
      {/* Header */}
      <div style={styles.userCardHeader} onClick={() => setExpanded(e => !e)}>
        <div style={styles.userInfo}>
          {userData.photoURL ? (
            <img src={userData.photoURL} alt="" style={styles.avatar}
              onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={styles.avatarFallback}>{initials}</div>
          )}
          <div>
            <div style={styles.userName}>
              {userData.displayName || userData.email?.split('@')[0] || 'Unknown'}
              {hasGrants && (
                <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--ka-gold)', fontWeight: 600 }}>
                  ✦ {allowedTools.length} granted
                </span>
              )}
              {nominated && !isAlreadySuperAdmin && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>
                  ⏳ pending promotion
                </span>
              )}
            </div>
            <div style={styles.userMeta}>{userData.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isOwner ? (
            <span style={styles.ownerBadge}>👑 Owner</span>
          ) : isAlreadySuperAdmin ? (
            <span style={styles.superAdminBadge}>⚡ Super Admin</span>
          ) : (
            <span style={styles.roleBadge}>
              {ROLE_LABELS[userData.role] || userData.role || 'No role'}
            </span>
          )}
          <span style={{ ...styles.chevron, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>
      </div>

      {/* Expanded: tool toggles */}
      {expanded && (
        <>
          <div style={styles.toolGrid}>
            {TOOLS.map(tool => {
              const isOn = isToolOn(tool);
              const isGranted = allowedTools.includes(tool.id);
              return (
                <div key={tool.id} style={{
                  ...styles.toolRow,
                  borderColor: !isOwner && isGranted ? 'var(--ka-gold)' : '#e5e7eb',
                  background: !isOwner && isGranted ? 'rgba(200,151,58,0.05)' : '#f9fafb',
                }}>
                  <div>
                    <div style={styles.toolLabel}>
                      <ToolIcon tool={tool} />{tool.label}
                    </div>
                    <div style={styles.toolSource}>
                      {tool.default ? 'default — all faculty' : isGranted ? 'manually granted' : 'not granted'}
                    </div>
                  </div>
                  <Toggle on={isOn} onChange={() => handleToolToggle(tool)} disabled={saving || tool.default || isOwner} />
                </div>
              );
            })}
          </div>

          {!isOwner && (
          <div style={styles.saveIndicator}>
            {saving ? 'Saving…' : savedRecently ? '✓ Saved' : hasGrants ? `${allowedTools.length} tool(s) manually granted` : 'No manual grants — default access only'}
          </div>
)}

          {/* Role + Departments — surfaces direct controls for two of the most
              common admin tweaks. Hidden on the owner account entirely. */}
          {!isOwner && (
            <RoleAndDepartmentsBlock
              userData={{ ...userData, role: currentRole }}
              recognizedDepartments={recognizedDepartments}
              recognizedDepartmentsLoading={recognizedDepartmentsLoading}
              onRoleChange={(newRole) => setCurrentRole(newRole)}
            />
          )}

          {/* Super Admin management — never shown for owner or current user */}
          {!isOwner && !isCurrentUser && (
            <div style={styles.promoteSection}>
              {isAlreadySuperAdmin ? (
                // ── Revoke existing superadmin ────────────────────────────────
                <div style={{ width: '100%' }}>
                  {!confirmRevoke ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div style={styles.promoteInfo}>
                        <strong>{userData.displayName || userData.email}</strong> currently has Super Admin access.
                      </div>
                      <button
                        style={{ ...styles.promoteBtn, background: '#fee2e2', color: '#ef4444' }}
                        onClick={() => setConfirmRevoke(true)}
                      >
                        Revoke Super Admin
                      </button>
                    </div>
                  ) : (
                    <div style={styles.confirmBox}>
                      <strong>Are you sure?</strong> This will remove Super Admin access from{' '}
                      <strong>{userData.displayName || userData.email}</strong> and revert them to Faculty.
                      They will lose access to the Super Admin panel immediately.
                      <div style={styles.confirmActions}>
                        <button
                          style={{ ...styles.promoteBtn, background: '#ef4444', color: '#fff' }}
                          onClick={handleRevokeSuperAdmin}
                          disabled={revokingSuperAdmin}
                        >
                          {revokingSuperAdmin ? 'Revoking…' : 'Yes, revoke access'}
                        </button>
                        <button
                          style={{ ...styles.promoteBtn, background: '#e5e7eb', color: '#374151' }}
                          onClick={() => setConfirmRevoke(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // ── Promote flow (nomination + invite code) ───────────────────
                <>
                  {!nominated ? (
                    <>
                      <div style={styles.promoteInfo}>
                        <strong>Promote to Super Admin</strong> — Step 1 of 2.
                        Nominating unlocks invite code generation.
                        They won't have access until they enter the code.
                      </div>
                      <button
                        style={{ ...styles.promoteBtn, background: 'var(--ka-navy)', color: '#fff' }}
                        onClick={handleNominate}
                      >
                        Nominate for Promotion
                      </button>
                    </>
                  ) : (
                    <div style={{ width: '100%' }}>
                      <div style={styles.promoteInfo}>
                        <strong>Step 2 of 2</strong> — Generate an invite code and send it to{' '}
                        <strong>{userData.displayName || userData.email}</strong> out-of-band.
                        They'll enter it at <code>/superadmin-accept</code>. Expires in {CODE_TTL_HOURS}h.
                      </div>
                      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                        <button
                          style={{ ...styles.promoteBtn, background: 'var(--ka-gold)', color: 'var(--ka-navy)' }}
                          onClick={handleGenerateCode}
                          disabled={generatingCode}
                        >
                          {generatingCode ? 'Generating…' : inviteCode ? 'Regenerate Code' : 'Generate Invite Code'}
                        </button>
                        <button
                          style={{ ...styles.promoteBtn, background: '#fee2e2', color: '#ef4444' }}
                          onClick={handleRevokeNomination}
                          disabled={revoking}
                        >
                          {revoking ? 'Revoking…' : 'Revoke Nomination'}
                        </button>
                      </div>

                      {inviteCode && (
                        <div style={{ ...styles.codeBox, marginTop: 12, marginLeft: 0, marginRight: 0 }}>
                          <div style={styles.codeLabel}>Invite Code — send to {userData.displayName || userData.email}</div>
                          <div style={styles.codeValue}>{inviteCode.code}</div>
                          <div style={styles.codeExpiry}>
                            Expires: {inviteCode.expiresAt.toLocaleString()} ({CODE_TTL_HOURS}h window)
                          </div>
                          <button style={styles.revokeBtn} onClick={handleRevokeNomination}>
                            Cancel &amp; revoke this code
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Explanation for protected users */}
          {(isOwner || isCurrentUser) && (
            <div style={{ ...styles.promoteSection, color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
              {isOwner
                ? '👑 Owner account — Super Admin status cannot be modified.'
                : '🔒 Your own account — Super Admin status cannot be self-modified.'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export default function SuperAdminPanel() {
  const { isSuperAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Recognized departments — single fetch, shared across all UserCards.
  // Used by RoleAndDepartmentsBlock to populate the "Add department"
  // dropdown. Falls back to an empty list on failure (the Custom path
  // still works).
  const [recognizedDepartments, setRecognizedDepartments] = useState([]);
  const [recognizedDepartmentsLoading, setRecognizedDepartmentsLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      list.sort((a, b) =>
        (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')
      );
      setUsers(list);
      setLoading(false);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'getRecognizedDepartments');
        const res = await fn({});
        setRecognizedDepartments(res?.data?.departments || []);
      } catch (err) {
        console.error('getRecognizedDepartments failed:', err);
      } finally {
        setRecognizedDepartmentsLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return users;
    return users.filter(u =>
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>⚡ Super Admin</h1>
        <p style={styles.subtitle}>
          Grant tool access and promote or revoke Super Admin status via the two-step invite flow.
          Cards marked <strong>✦ granted</strong> have manually granted tools.
        </p>
      </div>

      {/* System-wide settings */}
      <h2 style={styles.sectionTitle}>System</h2>

      <CollapsibleSection
        icon="✉️"
        title="Email Template Manager"
        subtitle="Compose, preview, and create/update KA email templates"
      >
        <EmailTemplateManager />
      </CollapsibleSection>

      <CollapsibleSection
        icon="📨"
        title="Absence Email Trigger"
        subtitle="Manually preview/send unexcused absence emails (temporary — remove before rollout)"
      >
        <AbsenceEmailTrigger />
      </CollapsibleSection>

      <CollapsibleSection
        icon="📊"
        title="Grade Pull Trigger"
        subtitle="GradeGarden → /grades ingestion (temporary — remove before rollout)"
      >
        <GradePullTrigger />
      </CollapsibleSection>

      <CollapsibleSection
        icon="🧭"
        title="Advisor Digest Trigger"
        subtitle="Advisor weekly check-in email (temporary — remove before rollout)"
      >
        <AdvisorDigestTrigger />
      </CollapsibleSection>

      <CollapsibleSection
        icon="🔄"
        title="Roster Sync Trigger"
        subtitle="Pull students/staff from Veracross webhooks and reconcile /students + /staff"
      >
        <RosterSyncTrigger />
      </CollapsibleSection>

            <CollapsibleSection
        icon="🔍"
        title="Roster Sync Diagnostic"
        subtitle="Read-only — estimate what a live sync would create/deactivate before writing"
      >
        <RosterSyncDiagnostic />
      </CollapsibleSection>

      <SystemSettingsAccordion user={user} />
      <CheckoutPermissionsAccordion />
      <CheckinPermissionsAccordion />
      <BulkRecordCreationAccordion />
      <SGCPromoteDraftsPanel />






      {/* User management */}
      <h2 style={styles.sectionTitle}>Users</h2>

      <div style={styles.searchWrap}>
        <span style={styles.searchIcon}>🔍</span>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search by name, email, or role…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {!loading && (
        <div style={styles.resultCount}>
          {filtered.length === users.length
            ? `${users.length} user${users.length !== 1 ? 's' : ''}`
            : `${filtered.length} of ${users.length} users`}
        </div>
      )}

      {loading && <div style={styles.loadingWrap}>Loading users…</div>}

      {!loading && filtered.length === 0 && (
        <div style={styles.emptyState}>
          {search ? `No users match "${search}"` : 'No users found.'}
        </div>
      )}

      {filtered.map(u => (
        <UserCard
          key={u.uid}
          userData={u}
          recognizedDepartments={recognizedDepartments}
          recognizedDepartmentsLoading={recognizedDepartmentsLoading}
        />
      ))}
    </div>
  );
}

const fieldLabelStyle = {
  display:      "block",
  fontSize:     12,
  fontWeight:   700,
  color:        "#233C5F",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 4,
  marginTop:    12,
};

const inputStyle = {
  width:        "100%",
  padding:      "6px 8px",
  border:       "1px solid #d1d5db",
  borderRadius: 4,
  fontSize:     13,
  boxSizing:    "border-box",
};

const fieldHintStyle = {
  fontSize:   11,
  color:      "#888",
  fontStyle:  "italic",
  marginTop:  4,
};